// 选中的草图实体, 偏移的参数
// 偏移后的预览
// 确定, 去除预览模型, 渲染最终偏移后草图实体

import type { Subscription } from 'rxjs';
import { SketchEntityEquidistantOffset } from './SketchEntityEquidistantOffset';
import { SketchIsometricEntitiesPreview } from './SketchIsometricEntitiesPreview';
import { SketchOffsetCoverFactory } from './SketchOffsetCoverFactory';
import type { Viewer } from '../../viewer';
import type { ReferencePlane } from '../../reference';
import { AbstractSketchEdgeObject, SketchLineObject, SketchArcObject, SketchCircleObject, SketchEllipseObject, SketchParabolaObject, SketchSplineObject, SketchStyleSplineObject, SketchGroup, SketchSlotObject, SketchPointObject, EllipseControlPointRole } from '../../mesh';
import {
    EntityInteractionEventType,
    type SelectionUpdatedEventPayload,
} from '../../manager/EntitySelectionManager';
import { Object3D, Vector2, Vector3 } from 'three/webgpu';
import { CombineSketchEntitiesUnifiedDirection } from './CombineSketchEntitiesUnifiedDirection';
import { SketchEntitiesGroup } from './SketchEntitiesSort';
import { SketchEntityIntersect } from './SketchEntityIntersect';
import { SketchExtensionAndIntersectionProcessing } from './SketchExtensionAndIntersectionProcessing';
import { SketchEntityPreviewOverlayDashLine } from './SketchEntityPreviewOverlayDashLine';
import { SketchOffsetClosedProfileFinalizer } from './SketchOffsetClosedProfileFinalizer';
import { isSketchArcObject, isSketchCollectionObject, isSketchEllipseObject, isSketchCircleObject, isSketchLineObject, isSketchParabolaObject, isSketchSplineObject, isSketchStyleSplineObject } from '../../mesh/guards';
import { LineParallelConstraint, PointCoincidentConstraint } from '../solver/constraint/relationDefinition';
import type { SketchDimensionInBatchCreationResult } from '../dimension/application/SketchDimensionApplicationService';
import type { CreateSketchDimensionInput, SketchDimensionMetadata } from '../dimension';
import {
    resolveEllipseAnchorPointAtAngle,
    resolveEllipseArcMidAngle,
} from '../dimension/utils';
import type {SketchEllipseAnchorGeometry} from '../dimension/utils';
import type { EntityPatch } from '../../history/types';

import type { CoverType, OffsetCapRecord, OffsetCandidate, SketchEntityChain, OffsetChainRecord } from './type';

/**
 * 等距链落地草稿（预览阶段登记，落地创建尺寸时组装成 {@link OffsetChainRecord}）。
 *
 * 说明：预览阶段尚未分配尺寸 ID，只能以链首落地实体 uuid 为键登记链成员与方向；
 * 落地阶段创建链首距离尺寸时，按链首 uuid 取出草稿并写入尺寸 metadata。
 */
interface OffsetChainDraftRecord {
    /** 源链有序实体持久 ID。 */
    readonly sourceEntityIds: string[];
    /** 等距链有序实体持久 ID。 */
    readonly offsetEntityIds: string[];
    /** 偏移方向：创建该遍预览时的全局反向开关。 */
    readonly globalIsReverse: boolean;
    /** 顶端加盖记录：落地创建盖子实体后回填，随链首距离尺寸 metadata 持久化（尺寸联动重算用）。 */
    readonly caps: OffsetCapRecord[];
}

/**
 * 顶端加盖链登记（预览阶段登记，落地阶段创建盖子实体）。
 *
 * 说明：
 * 1. 仅当启用顶端加盖（options.cover.hasCover）且链为开环（isChainClosed === false）时登记；
 * 2. 双向偏移对同一条源链登记两条记录（左右偏移链），落地时按 sourceChainKey 配对，
 *    盖子连接两条偏移链的对应端点（源链不参与盖子）；
 * 3. 记录保存源链与偏移链引用，供落地阶段推导自由端与胶囊外侧方向。
 */
interface OffsetCoverChainRecord {
    /** 源链（自由端侧推导与外侧方向计算用）。 */
    readonly sourceChain: SketchEntityChain;
    /** 偏移链（延伸/相交处理后的最终链）。 */
    readonly offsetChain: SketchEntityChain;
    /** 源链持久 ID 组合键（双向配对：左右偏移链共享同一源链）。 */
    readonly sourceChainKey: string;
}

/**
 * 解析完成的单个盖子连接（首端盖或尾端盖）。
 *
 * 说明：由 {@link SketchOffsetController.resolveCoverConnections} 从
 * {@link OffsetCoverChainRecord} 分组推导得到，预览阶段与落地阶段共用，
 * 保证盖子预览与落地实体连接同一对端点、鼓包朝向一致。
 */
interface ResolvedCoverConnection {
    /** 首条登记记录对应链的草稿键（链首落地实体 uuid）。 */
    readonly draftKey: string;
    /** 双向时配对链的草稿键；单向为 null。 */
    readonly draftKeyB: string | null;
    /** 盖端点 A（第一条记录侧）。 */
    readonly startPoint: SketchPointObject;
    /** 盖端点 B（对侧：单向=源链端点；双向=配对偏移链端点）。 */
    readonly endPoint: SketchPointObject;
    /** 圆弧盖胶囊外侧方向（世界坐标）。 */
    readonly outwardWorld: Vector3;
}

interface SketchOffsetControllerOptions {
    sketchEntityChains?: SketchEntityChain[]
    distance: number    // 偏移的长度, 单位mm
    isReverse: boolean // 是否反向
    isAddSize: boolean // 是否添加尺寸
    isOpenSelectChain: boolean // 是否开启选择链
    isBidirectional: boolean // 是否双向
    cover: {                 // 盖子
        hasCover: boolean;
        type: CoverType;
    }
    isBasicGeometryBody: boolean // 是否为基本几何体（落地时源实体转为构造线）
    isOffsetGeometryBody: boolean // 是否为偏移几何体（落地时偏移实体转为构造线）
}

/** 默认偏移选项。 */
const DEFAULT_OPTIONS: SketchOffsetControllerOptions = {
    distance: 10,
    isReverse: false,
    isAddSize: true,
    isOpenSelectChain: true,
    isBidirectional: false,
    cover: {
        hasCover: false,
        type: "circularArc",
    },
    isBasicGeometryBody: false,
    isOffsetGeometryBody: false,
};

export class SketchOffsetController {
    private readonly viewer: Viewer;

    /** 按选择链分组的原始草图实体。每个内层数组是一个连通分量（通过端点相连的一组实体），
     * 不相连的实体各自独立成组。等距实体操作将以链为单位处理，
     * 后续可在链内相邻实体之间插入盖子（圆弧/直线过渡）。 */
    sketchEntityChains: SketchEntityChain[] = [];
    /** 偏移后最终草图实体（由 SketchEntityEquidistantOffset 直接产出）。 */
    offsetedSketchEntities: OffsetCandidate[] = [];
    previewSketchEntities: Object3D[] = [];  // 预览 Three.js Line 对象

    /**
     * 等距实体 uuid → 源实体映射。
     *
     * 说明：
     * 1. 等距圆弧/椭圆弧落地时需要与源实体圆心创建`重合`关系，而延伸/相交处理
     *    会原位替换实体实例，仅凭对象身份无法反查源实体；
     * 2. 本映射在预览生成阶段按"处理链 ↔ 被保留原始链"的索引对应关系登记，
     *    落地阶段按最终实体 uuid 查询，处理链整链丢弃时其源链也不进映射。
     */
    private offsetSourceEntityMap = new Map<string, OffsetCandidate>();

    /**
     * 预览阶段「基本几何体」临时构造线恢复映射（源实体 uuid → 原状态记录）。
     *
     * 说明：
     * 1. 勾选「基本几何体」后，预览阶段需把参与偏移的源实体临时切换为构造线
     *    样式，实现与 SolidWorks 一致的即时反馈；
     * 2. 本映射登记被临时切换的源实体及其原 isConstruction 值，供重新生成预览、
     *    退出命令、落地失败时恢复原样（预览阶段不修改历史状态）；
     * 3. 落地成功后源实体最终构造线状态由历史事务整组快照确定，先清空本映射
     *    再清理预览，避免恢复逻辑覆盖落地结果。
     */
    private previewBasicGeometrySourceRestore = new Map<
        string,
        { object: AbstractSketchEdgeObject; originalIsConstruction: boolean }
    >();

    /**
     * 每条选择链第一个落地实体的 uuid 集合。
     *
     * 说明：
     * 1. 组合图形（一条链多个实体）开启“添加尺寸”时，每对源/偏移实体都创建
     *    距离尺寸；链首尺寸显示智能尺寸 UI，其余链成员尺寸隐藏 UI（只保留求解约束）；
     * 2. 本集合在预览生成阶段按“处理链内 entityIndex === 0”登记（处理链只原位
     *    替换、不重排，链首实体位置稳定），落地阶段据此区分链首与隐藏尺寸；
     * 3. 双向模式对左右两条偏移轮廓各执行一遍登记，因此双向时每条轮廓各一个可见尺寸。
     */
    private offsetChainFirstEntityUuids = new Set<string>();

    /**
     * 链首落地实体 uuid → 等距链落地草稿。
     *
     * 说明：预览阶段（generatePreviewInDirection 阶段四）按链首实体登记整条链的
     * 源/等距实体持久 ID 与全局反向开关；落地阶段创建链首距离尺寸时，按链首 uuid
     * 取出草稿组装为 {@link OffsetChainRecord} 写入尺寸 metadata，供
     * OffsetChainDerivedSyncService 在尺寸改值时整链派生重算。
     */
    private offsetChainDraftRegistry = new Map<string, OffsetChainDraftRecord>();

    /**
     * 顶端加盖链登记：链首落地实体 uuid → 盖子链信息。
     *
     * 说明：预览阶段（generatePreviewInDirection 阶段四）仅对开环链登记，
     * 落地阶段（createCoverEntities）按 sourceChainKey 分组后为每条链创建两个盖子实体。
     */
    private coverChainRegistry = new Map<string, OffsetCoverChainRecord>();

    /** 本次落地批次创建的尺寸结果（补丁待父事务统一提交）。 */
    private pendingDimensionBatchResults: SketchDimensionInBatchCreationResult[] = [];

    /** 已移交父事务、等待提交结果期间保留的对称回滚句柄。 */
    private inFlightDimensionRollbacks: Array<() => void> = [];

    /** 当前偏移选项。 */
    private options: SketchOffsetControllerOptions = { ...DEFAULT_OPTIONS };

    /** 所有事件订阅，统一管理生命周期。 */
    private readonly subscriptions: Subscription[] = [];

    /** 预览虚线与上色轮廓关系判定器（阶段三：收集轮廓、判定 inside/outside、分割预览段）。 */
    private readonly previewOverlayDashLine: SketchEntityPreviewOverlayDashLine;

    /** 落地闭合轮廓收口器（多实体闭环统一走草图组闭合入口，创建闭合集合与上色轮廓）。 */
    private readonly closedProfileFinalizer = new SketchOffsetClosedProfileFinalizer();

    /**
     * 获取当前激活草图组的参考面。
     * 说明：从 viewer.sketchGroupManager.activeSketchGroup 动态获取，
     * 确保在草图切换后也能拿到正确的平面。
     */
    get referencePlane(): ReferencePlane | null {
        return this.viewer.sketchGroupManager?.activeSketchGroup?.referencePlane ?? null;
    }

    get activeSketchGroup(): SketchGroup | null {
        return this.viewer.sketchGroupManager.activeSketchGroup ?? null;
    }

    constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.previewOverlayDashLine = new SketchEntityPreviewOverlayDashLine(viewer);
    }

    // ═══════════════════════════════════════════════════════════
    // 监听
    // ═══════════════════════════════════════════════════════════

    /**
     * 绑定选中变化监听。
     * 说明：
     * 1. 订阅 entitySelectionManager 的 SelectionUpdated 事件；
     * 2. 每次选中/取消选中任意草图实体时，自动触发偏移预览刷新；
     * 3. 防止重复绑定。
     */
    bindEvents(): void {
        if (this.subscriptions.length > 0) return;

        const selectionManager = this.viewer.entitySelectionManager;
        if (selectionManager) {
            this.subscriptions.push(
                selectionManager
                    .observe(EntityInteractionEventType.SelectionUpdated)
                    .subscribe(this.handleSelectionUpdated),
            );
        }
    }

    /**
     * 解绑所有事件订阅。
     */
    unbindEvents(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /**
     * 选中变化回调。
     * 说明：
     * 1. 不管选中还是取消选中，都会触发；
     * 2. 先调 getPickedEntities 同步选中的实体列表；
     * 3. 再调 generatePreviewSketchEntity 刷新偏移预览。
     * @param _payload 选中事件载荷。
     */
    private readonly handleSelectionUpdated = (_payload: SelectionUpdatedEventPayload): void => {
        this.getPickedEntities();
        this.generatePreviewSketchEntity();
    };

    // ═══════════════════════════════════════════════════════════
    // 生命周期
    // ═══════════════════════════════════════════════════════════

    /**
     * 启动等距实体模式。
     *
     * 说明：仅在命令激活时调用一次，负责绑定事件监听并进入模式。
     * 后续参数变更应通过 {@link updateOptions} 进行，避免重置已选实体。
     *
     * @param options 偏移参数补丁，与默认值合并。
     */
    startMode(options: Partial<SketchOffsetControllerOptions> = {}): void {
        this.options = { ...DEFAULT_OPTIONS, ...options };
        // 双向偏移时"反向"无意义，启动参数同样需要收口，
        // 避免外部在 startMode 阶段传入"双向 + 反向"非法组合。
        if (this.options.isBidirectional) {
            this.options.isReverse = false;
        }
        // 外部显式传入选择链时同样收口：链中若含 SketchCollectionObject，
        // 先展开为其组成实体再进入等距实体列表
        this.sketchEntityChains = (this.options.sketchEntityChains ?? [])
            .map((chain) => this.normalizeOffsetCandidates(chain));
        this.bindEvents();
        //------ 命令激活吸收预选实体 开始------
        /**
         * 与 SolidWorks「先选中实体再点等距实体」一致：进入命令时自动吸收
         * 命令激活前已在普通草图选择链中选中的草图实体。
         * 1. 仅在确实存在选中实体时才以选择管理器种子重建选择链，避免覆盖
         *    外部通过 options.sketchEntityChains 显式传入的链（SDK 直接启动场景）；
         * 2. 吸收后立即生成黄色偏移预览，无需等待下一次 SelectionUpdated 事件。
         */
        const preselectedSeeds = this.viewer.entitySelectionManager
            ?.getSelectedObjects() as OffsetCandidate[] | undefined;
        if (preselectedSeeds && preselectedSeeds.length > 0) {
            this.getPickedEntities();
            this.generatePreviewSketchEntity();
        }
        //------ 命令激活吸收预选实体 结束------
        this.viewer.drawingNoticeCallback?.(
            `已进入等距实体`,
            'info',
        );
    }

    /**
     * 更新等距实体选项（不重置已选实体）。
     *
     * 说明：
     * 1. 属性面板每次修改参数（距离/方向/双向等）时调用本方法，而不是重复调用 {@link startMode}；
     * 2. 非选择链参数仅更新选项并刷新预览，已通过选择事件收集的 sketchEntityChains 保持不变；
     * 3. 当 isOpenSelectChain 开关发生变化时，会重新调用 {@link getPickedEntities} 扩展或收缩链选中列表；
     * 4. 若此时没有任何已选实体，直接跳过预览生成。
     *
     * @param options 偏移参数补丁，与当前选项合并。
     */
    updateOptions(options: Partial<SketchOffsetControllerOptions>): void {
        const prevIsOpenSelectChain = this.options.isOpenSelectChain;
        this.options = { ...this.options, ...options };

        // 双向偏移时"反向"无意义（与 SolidWorks 一致：勾选双向后反向失效）。
        // 在合并完成后统一强制复位，保证任何调用入口（属性面板/外部 API）
        // 都不会出现"双向 + 反向"的非法组合状态。
        if (this.options.isBidirectional) {
            this.options.isReverse = false;
        }

        // 当选择链开关发生变化时，需要重新计算选中实体列表（扩展或收缩链）
        if (options.isOpenSelectChain !== undefined && options.isOpenSelectChain !== prevIsOpenSelectChain) {
            this.getPickedEntities();
        }

        // 清空旧预览、刷新新预览（仅当存在已选实体时）
        this.clearPreviewSketchEntities();
        if (this.sketchEntityChains.length > 0) {
            this.generatePreviewSketchEntity();
        }
    }

    /**
     * 上色草图轮廓开关变化后的预览刷新入口。
     *
     * 说明：
     * 1. 「上色草图轮廓」开关会增删活动草图组中的 SketchContour 显示对象，
     *    而等距预览的虚线/实线标记在预览生成时已固化到预览 Line 材质上，
     *    开关变化后必须重跑一次预览生成，让阶段三重新收集轮廓并重建线型；
     * 2. 由 {@link Viewer.setShadedSketchContoursEnabled} 在轮廓显示同步完成后调用，
     *    关闭轮廓时重新收集不到轮廓 → 全部标记 outside → 虚线恢复为实线；
     * 3. 仅在存在已选实体（已有预览）时刷新；未进入模式或无选中实体时直接返回，
     *    后续选中实体会经 SelectionUpdated 事件按最新轮廓状态重建预览；
     * 4. 与 {@link updateOptions} 的刷新口径一致，复用 {@link generatePreviewSketchEntity}
     *    完整链路（清理旧预览 → 重建标记 → 重新创建预览线）。
     */
    public refreshPreviewAfterShadedSketchContoursChanged(): void {
        // 无已选实体说明当前没有等距预览，无需刷新
        if (this.sketchEntityChains.length === 0) {
            return;
        }
        this.generatePreviewSketchEntity();
    }

    /** 清理等距实体模式，解绑监听。 */
    clearMode(): void {
        // 恢复预览阶段的「基本几何体」临时构造线（取消命令时源实体回到原样式）
        this.restorePreviewBasicGeometryConstruction();
        this.sketchEntityChains = [];      // 按选择链分组的原始草图实体
        this.offsetedSketchEntities = []; // 偏移后草图实体
        this.offsetSourceEntityMap.clear(); // 等距实体到源实体的映射
        this.offsetChainFirstEntityUuids.clear(); // 链首落地实体 uuid 集合
        this.offsetChainDraftRegistry.clear(); // 等距链落地草稿
        this.coverChainRegistry.clear(); // 顶端加盖链登记
        this.pendingDimensionBatchResults = []; // 未提交的尺寸批次
        this.inFlightDimensionRollbacks = []; // 在途尺寸回滚句柄
        this.previewSketchEntities = [];  // 预览草图实体
        this.previewOverlayDashLine.reset(); // 清理轮廓收集结果与预览标记
        this.unbindEvents();

        this.viewer.drawingNoticeCallback?.(
            `已退出等距实体`,
            'info',
        );
    }

    /** 释放所有资源。 */
    dispose(): void {
        // 兜底恢复预览临时构造线，防止应用销毁时源实体样式残留
        this.restorePreviewBasicGeometryConstruction();
        this.unbindEvents();
    }

    // ═══════════════════════════════════════════════════════════
    // 业务方法
    // ═══════════════════════════════════════════════════════════

    /**
     * 生成预览草图实体。
     * 
     * 说明：
     * 1. 每次生成前统一清理旧预览、落地候选、源实体映射与虚线标记状态；
     * 2. 双向模式等价于对同一批源实体执行两次独立、方向相反的等距实体预览：
     *    第一遍不反向（isReverse=false），第二遍反向（isReverse=true），
     *    两遍结果追加合并（预览对象与落地候选均保留），单方向内部数据流不变；
     * 3. 单方向流程：先通过 {@link CombineSketchEntitiesUnifiedDirection.computeChainEffectiveReverseFlags}
     *    为链中每个实体计算逐实体 isReverse，再逐实体偏移、延伸/相交处理、
     *    与上色图形求交标记虚线，最后生成轻量 Three.js Line 预览对象加入场景。
     */
    generatePreviewSketchEntity() {
        // 每次生成新预览前先清理旧预览，避免累积泄漏
        this.clearPreviewSketchEntities();
        // 恢复上一轮「基本几何体」预览临时构造线（若有），
        // 使源实体回到进入预览前的样式，随后按最新选项重新应用
        this.restorePreviewBasicGeometryConstruction();
        this.offsetedSketchEntities = [];
        this.offsetSourceEntityMap.clear(); // 同步清理源实体映射，避免上一轮预览残留
        this.offsetChainFirstEntityUuids.clear(); // 同步清理链首实体登记，避免上一轮预览残留
        this.offsetChainDraftRegistry.clear(); // 同步清理等距链草稿，避免上一轮预览残留
        this.coverChainRegistry.clear(); // 同步清理顶端加盖链登记，避免上一轮预览残留
        this.pendingDimensionBatchResults = []; // 同步清理未提交的尺寸批次
        // 重建关系标记与分割映射，避免上一轮预览的标记残留
        this.previewOverlayDashLine.reset();

        const refPlane = this.referencePlane;
        if (this.sketchEntityChains.length === 0 || !refPlane) return;

        // 双向：两次独立、方向相反的等距实体调用；第二遍内部使用反向参数，
        // 不改动 this.options.isReverse（保持"双向时反向选项强制复位"的收口语义）。
        // 单方向：保持原有行为，使用用户当前的 isReverse 选项。
        const directions: boolean[] = this.options.isBidirectional
            ? [false, true]
            : [this.options.isReverse];
        for (const isReverse of directions) {
            this.generatePreviewInDirection(isReverse, refPlane);
        }

        // 勾选「基本几何体」时，预览阶段把参与偏移的源实体临时切换为构造线样式
        this.applyPreviewBasicGeometryConstruction();

        // 顶端加盖预览：必须在双向两遍偏移预览完成后创建——
        // 双向盖连接左右两条偏移链的对应端点，第二遍登记完成前
        // 同 sourceChainKey 的配对记录不齐全，无法解析盖连接
        this.createCoverPreviews(refPlane);

        this.viewer.requestRender();
    }

    /**
     * 按指定反向参数生成单方向偏移预览。
     *
     * 说明：
     * 1. 阶段一：为每条链独立偏移每个实体，构建偏移后实体链；
     * 2. 阶段二：调用 {@link SketchExtensionAndIntersectionProcessing.sketchExtensionAndIntersectionProcessing}
     *    处理偏移实体之间的断开/相交/相连问题，得到连续轮廓；
     * 3. 阶段三：与上色图形求交，对相交/包含段标记虚线（{@link SketchEntityPreviewOverlayDashLine}）；
     * 4. 阶段四：生成预览 Line 加入场景，并把最终等距实体登记为落地候选、
     *    按位序登记源实体映射（供落地阶段创建圆心重合关系）。
     * 双向模式下由 {@link generatePreviewSketchEntity} 对本方法执行两遍
     * （isReverse 分别传 false / true），两遍结果追加合并。
     *
     * @param isReverse 该遍是否反向偏移。
     * @param refPlane 当前激活草图组的参考面（由外层统一获取，避免重复读取）。
     */
    private generatePreviewInDirection(isReverse: boolean, refPlane: ReferencePlane): void {
        // 阶段一：为每条链独立偏移每个实体，构建偏移后实体链
        const offsetChains: SketchEntityChain[] = [];
        const originalChainsForProcessing: SketchEntityChain[] = []; // 保存原始链引用，供阶段二使用

        for (const chain of this.sketchEntityChains) {
            // 这里增加一条判断, 如果chain是槽口, 则在此处将其展开为四个子草图实体
            if (chain[0].objectType === 'Slot') {
                let tmpSlotChildSketchs = (chain[0] as SketchSlotObject).getEdges();
                chain.pop();    // 删除槽口草图实体
                for (let i = 0; i < tmpSlotChildSketchs.length; i++) {
                    chain.push(tmpSlotChildSketchs[i]); // 将槽口草图实体替换为组成其的四个子草图实体
                }
            }

            // 为该选择链中的每个实体计算逐实体 effectiveIsReverse
            // （方向统一逻辑已抽离至 CombineSketchEntitiesUnifiedDirection，
            //   闭合链射线法 / 开口链传播 / 无弧链行进方向修正均在内部完成）
            const effectiveReverseMap = CombineSketchEntitiesUnifiedDirection.computeChainEffectiveReverseFlags(
                chain,
                refPlane,
                isReverse,
            );

            const offsetChain: OffsetCandidate[] = [];
            let chainValid = true;

            for (const entity of chain) {
                const effectiveIsReverse = effectiveReverseMap.get(entity.uuid) ?? isReverse;
                const offsetEntity = this.offsetSingleEntity(entity, effectiveIsReverse);
                if (!offsetEntity) {
                    chainValid = false;
                    break; // 单个实体偏移失败（如向内半径≤0），丢弃整条链
                }
                offsetChain.push(offsetEntity);
            }

            if (chainValid && offsetChain.length > 0) {
                offsetChains.push(offsetChain);
                originalChainsForProcessing.push(chain); // 保存对应的原始链
            }
        }

        // 构建闭合标志数组（与 offsetChains 索引一一对应），供阶段二使用
        // 注意：必须从 originalChainsForProcessing 构建，而非 sketchEntityChains，
        // 因为偏移失败的链会被跳过，导致 originalChainsForProcessing 更短
        const closedFlags = originalChainsForProcessing.map(chain => CombineSketchEntitiesUnifiedDirection.isChainClosed(chain));

        // 阶段二：延伸和相交处理，解决偏移实体之间的断开/交叉问题，去除冗余自交线段
        // keptOriginalChains 与 processedChains 按索引一一对应：整链失效被丢弃的原始链
        // 不会写入，阶段四据此把最终等距实体登记回其源实体（圆心重合关系需要）。
        const keptOriginalChains: SketchEntityChain[] = [];
        const processedChains = SketchExtensionAndIntersectionProcessing.sketchExtensionAndIntersectionProcessing(
            offsetChains,
            originalChainsForProcessing,
            closedFlags,
            keptOriginalChains,
        );

        // 阶段三: 判断与上色图形的相交情况, 对草图进行划分, 并添加是否被包含的标记
        // ① 收集上色草图轮廓对象；② 逐个实体判定（AABB 预判 → 精确求交 → 分割 → inside/outside 标记）；
        // ③ 相交/包含部分渲染为虚线（阶段四消费标记）——逻辑封装于 SketchEntityPreviewOverlayDashLine
        this.previewOverlayDashLine.process(processedChains.flat());


        // 阶段四：从处理后的链生成预览
        // 用带索引的循环把最终等距实体按位序登记回源实体：
        // 处理链内部仅原位替换、不重排，keptOriginalChains 与 processedChains
        // 逐链逐位对应（整链失效的原始链未写入 keptOriginalChains）。
        for (let chainIndex = 0; chainIndex < processedChains.length; chainIndex++) {
            const processedChain = processedChains[chainIndex]!;
            const sourceChain = keptOriginalChains[chainIndex] ?? [];
            for (let entityIndex = 0; entityIndex < processedChain.length; entityIndex++) {
                const entity = processedChain[entityIndex]!;
                const splitSegments = this.previewOverlayDashLine.splitSegmentsMap.get(entity.uuid);
                if (splitSegments && splitSegments.length > 0) {
                    // 被分割实体：每个分割段生成独立预览，线型按各段 inside/outside 标记决定
                    for (const segment of splitSegments) {
                        const preview = this.createPreviewForEntity(
                            segment,
                            this.previewOverlayDashLine.isdashedLineMark.get(segment.uuid) === 'inside',
                        );
                        if (!preview) continue;
                        this.previewSketchEntities.push(preview);
                        this.viewer.scene.add(preview);
                    }
                } else {
                    // 未分割实体：线型按实体自身标记决定
                    const preview = this.createPreviewForEntity(
                        entity,
                        this.previewOverlayDashLine.isdashedLineMark.get(entity.uuid) === 'inside',
                    );
                    if (!preview) continue;
                    this.previewSketchEntities.push(preview);
                    this.viewer.scene.add(preview);
                }
                // 最终等距实体始终是原始实体，与预览分割无关
                this.offsetedSketchEntities.push(entity);
                // 链首落地实体登记：链首用于组装整链草稿（源/等距实体持久 ID +
                // 全局反向开关）供落地组装尺寸 metadata；落地阶段每对实体都会创建距离尺寸，
                // 其中链首显示智能尺寸 UI，其余链成员隐藏 UI（只保留求解约束）。
                if (entityIndex === 0) {
                    this.offsetChainFirstEntityUuids.add(entity.uuid);
                    if (sourceChain.length === processedChain.length) {
                        this.offsetChainDraftRegistry.set(entity.uuid, {
                            sourceEntityIds: sourceChain.map((source) => source.persistentId),
                            offsetEntityIds: processedChain.map((offset) => offset.persistentId),
                            globalIsReverse: isReverse,
                            caps: [],
                        });
                        // 顶端加盖登记：仅开环链（闭合链已封闭无需封口，对齐 SOLIDWORKS）；
                        // 双向偏移对同一条源链登记两条记录，落地阶段按 sourceChainKey 配对生成盖子
                        if (this.options.cover.hasCover && !closedFlags[chainIndex]) {
                            this.coverChainRegistry.set(entity.uuid, {
                                sourceChain,
                                offsetChain: processedChain,
                                sourceChainKey: sourceChain.map((source) => source.persistentId).join('|'),
                            });
                        }
                    }
                }
                // 登记源实体映射（供落地阶段创建圆心重合关系）；位序上无对应源实体时跳过
                const sourceEntity = sourceChain[entityIndex];
                if (sourceEntity) {
                    this.offsetSourceEntityMap.set(entity.uuid, sourceEntity);
                }
            }
        }
    }

    /**
     * 对单个原始实体执行偏移，返回偏移后的实体。
     * 
     * 说明：从原 generatePreviewSketchEntity 中抽出的 offset 逻辑，
     * 去除了预览生成部分，仅负责计算偏移。
     * 
     * @param entity 原始草图实体。
     * @param effectiveIsReverse 该实体的有效偏移方向。
     * @returns 偏移后的实体，失败返回 null。
     */
    private offsetSingleEntity(entity: OffsetCandidate, effectiveIsReverse: boolean): OffsetCandidate | null {
        const entityType = entity.objectType;
        const refPlane = this.referencePlane;
        if (!refPlane) return null;

        switch (entityType) {
            case 'Line': {
                return SketchEntityEquidistantOffset.isometricSketchLineObject(
                    entity as SketchLineObject,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
            }

            case 'Arc': {
                const arcRes = SketchEntityEquidistantOffset.isometricSketchArcObject(
                    entity as SketchArcObject,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
                if (!arcRes) {
                    this.viewer.drawingNoticeCallback?.(
                        `偏移距离 (${this.options.distance}mm) 超过圆弧半径，无法向内偏移。`,
                        'warning',
                    );
                }
                return arcRes;
            }

            case 'Ellipse': {
                const ellipseRes = SketchEntityEquidistantOffset.isometricSketchEllipticalArc(
                    entity as SketchEllipseObject,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
                if (!ellipseRes) {
                    this.viewer.drawingNoticeCallback?.(
                        `偏移距离 (${this.options.distance}mm) 超过椭圆半轴，无法向内偏移。`,
                        'warning',
                    );
                }
                return ellipseRes;
            }

            case 'Parabola': {
                const parabolaRes = SketchEntityEquidistantOffset.isometricSketchParabolaObject(
                    entity as SketchParabolaObject,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
                if (!parabolaRes) {
                    this.viewer.drawingNoticeCallback?.(
                        `抛物线定义无效或偏移距离 (${this.options.distance}mm) 导致曲线自相交，无法偏移。`,
                        'warning',
                    );
                }
                return parabolaRes;
            }

            case 'Spline': {
                if (!(entity instanceof SketchSplineObject)
                    && !(entity instanceof SketchStyleSplineObject)) {
                    return null;
                }
                const splineRes = SketchEntityEquidistantOffset.isometricSketchSplineCurve(
                    entity,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
                if (!splineRes) {
                    this.viewer.drawingNoticeCallback?.(
                        `偏移距离 (${this.options.distance}mm) 导致样条曲线自相交，无法生成等距实体。`,
                        'warning',
                    );
                }
                return splineRes;
            }

            case 'Circle': {
                const circleRes = SketchEntityEquidistantOffset.isometricSketchCircleObject(
                    entity as SketchCircleObject,
                    this.options.distance,
                    effectiveIsReverse,
                    refPlane,
                );
                if (!circleRes) {
                    this.viewer.drawingNoticeCallback?.(
                        `偏移距离 (${this.options.distance}mm) 不合法，无法偏移。`,
                        'warning',
                    );
                }
                return circleRes;
            }

            default:
                this.viewer.drawingNoticeCallback?.(
                    `等距实体暂不支持该类型: ${entityType}`,
                    'warning',
                );
                return null;
        }
    }

    /**
     * 收集当前活动草图组内所有上色草图轮廓对象。
     * 为处理后的偏移实体创建预览 Three.js Line 对象。
     * 
     * 说明：从原 generatePreviewSketchEntity 中抽出的预览生成逻辑，
     * 仅负责从 SketchXxxObject 读取几何数据生成预览。
     * 
     * @param entity 偏移后的草图实体。
     * @param dashed 是否使用虚线样式（位于上色轮廓内部时 true）。
     * @returns 预览用 Three.js Line 对象。
     */
    private createPreviewForEntity(entity: OffsetCandidate, dashed = false): Object3D | null {
        const refPlane = this.referencePlane;
        if (!refPlane) return null;

        const entityType = entity.objectType;

        // 偏移几何体开启时，偏移实体预览整体以构造线（虚线）样式显示；
        // 与上色轮廓 inside 标记的虚线判定叠加（任一成立即渲染虚线）
        const useDashed = dashed || this.options.isOffsetGeometryBody;

        switch (entityType) {
            case 'Line':
                return SketchIsometricEntitiesPreview.createLinePreview(entity as SketchLineObject, useDashed);

            case 'Arc':
                return SketchIsometricEntitiesPreview.createArcPreview(entity as SketchArcObject, refPlane, useDashed);

            case 'Ellipse':
                return SketchIsometricEntitiesPreview.createEllipsePreview(entity as SketchEllipseObject, refPlane, useDashed);

            case 'Parabola':
                return SketchIsometricEntitiesPreview.createParabolaPreview(entity as SketchParabolaObject, useDashed);

            case 'Spline':
                return SketchIsometricEntitiesPreview.createSplinePreview(
                    entity as SketchSplineObject | SketchStyleSplineObject,
                    useDashed,
                );

            case 'Circle':
                return SketchIsometricEntitiesPreview.createCirclePreview(entity as SketchCircleObject, refPlane, useDashed);

            default:
                return null;
        }
    }

    /**
     * 创建顶端加盖实体（仅开环链，落地阶段调用）。
     *
     * 说明：
     * 1. 按 sourceChainKey 分组：单向偏移每组 1 条记录，盖子连接「源链端点 ↔ 偏移链端点」；
     *    双向偏移每组 2 条记录，盖子连接「左右两条偏移链的对应端点」（源链不参与盖子）；
     * 2. 每条开环链只加两个盖：链首自由端一个、链尾自由端一个（组合实体不逐实体加）；
     * 3. 盖子端点直接复用相邻实体（源链/偏移链）的自由端点控制点对象，共享即天然重合，
     *    无需额外挂重合关系；
     * 4. 圆弧盖按胶囊形规则弯曲（鼓包朝链端外侧），方向由源链走向推导；
     * 5. 创建的盖子实体登记进链首距离尺寸 metadata（draft.caps），
     *    供 OffsetChainDerivedSyncService 距离改值时联动重建。
     */
    private createCoverEntities(): void {
        if (!this.options.cover.hasCover) {
            return;
        }
        const refPlane = this.referencePlane;
        if (!refPlane) {
            return;
        }

        // 解析所有盖子连接（预览阶段共用同一解析链路，保证几何一致）
        for (const { first, last } of this.resolveCoverConnections()) {
            // 首端盖：单向 = 偏移首自由端 ↔ 源首自由端；双向 = 左偏移首自由端 ↔ 右偏移首自由端
            this.createCoverEntity(
                this.options.cover.type,
                refPlane,
                first.draftKey,
                first.draftKeyB,
                first.startPoint,
                first.endPoint,
                first.outwardWorld,
            );
            // 尾端盖
            this.createCoverEntity(
                this.options.cover.type,
                refPlane,
                last.draftKey,
                last.draftKeyB,
                last.startPoint,
                last.endPoint,
                last.outwardWorld,
            );
        }
    }

    /**
     * 解析所有待创建的盖子连接（预览阶段与落地阶段共用）。
     *
     * 说明：
     * 1. 按 sourceChainKey 分组：单向偏移每组 1 条记录；双向偏移同一条源链
     *    登记左右两条记录，归入同一组；
     * 2. 自由端侧由源链推导（偏移链与源链同序同拓扑，自由端「侧」一致），
     *    偏移链端点按侧取，规避延伸/相交处理不保证相邻实体共享端点对象的问题；
     * 3. 首端盖：单向连接「偏移链首自由端 ↔ 源链首自由端」，双向连接
     *    「左偏移链首自由端 ↔ 右偏移链首自由端」；尾端盖同理；
     * 4. 胶囊外侧方向：沿源实体在自由端处的切向延伸（首端取链首实体自由端
     *    切向反向，尾端取链尾实体自由端切向），圆弧盖鼓包朝链端外侧；
     *    切线不可用时回退「自由端→相连端」弦方向；
     * 5. 链首/尾实体无拓扑端点（如圆）时跳过该链（无法封口）。
     *
     * @returns 各源链的首端盖与尾端盖连接（不含解析失败被跳过的链）。
     */
    private resolveCoverConnections(): Array<{ first: ResolvedCoverConnection; last: ResolvedCoverConnection }> {
        const connections: Array<{ first: ResolvedCoverConnection; last: ResolvedCoverConnection }> = [];

        // 按源链组合键分组：双向偏移时同一条源链的左右偏移链归为一组
        const groups = new Map<string, Array<{ key: string; record: OffsetCoverChainRecord }>>();
        for (const [key, record] of this.coverChainRegistry.entries()) {
            const list = groups.get(record.sourceChainKey) ?? [];
            list.push({ key, record });
            groups.set(record.sourceChainKey, list);
        }

        for (const group of groups.values()) {
            const first = group[0]!;
            const second = group.length > 1 ? group[1]! : null;
            const sourceChain = first.record.sourceChain;
            const offsetChainA = first.record.offsetChain;
            const offsetChainB = second?.record.offsetChain ?? null;
            const isBidirectional = offsetChainB !== null;

            // 自由端侧：由源链推导（偏移链与源链同序同拓扑，自由端「侧」一致）；
            // 偏移链端点按侧取，规避延伸/相交处理不保证相邻实体共享端点对象的问题
            const sourceSides = CombineSketchEntitiesUnifiedDirection.getOpenChainFreeEndpointSides(sourceChain);
            if (!sourceSides.firstSide || !sourceSides.lastSide || !sourceSides.firstFree || !sourceSides.lastFree) {
                // 链首/尾实体无拓扑端点（如圆），无法封口，跳过该链
                continue;
            }
            const firstSourceFree = sourceSides.firstFree;
            const lastSourceFree = sourceSides.lastFree;
            const firstOffsetFreeA = CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(
                offsetChainA[0]!,
                sourceSides.firstSide,
            );
            const lastOffsetFreeA = CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(
                offsetChainA[offsetChainA.length - 1]!,
                sourceSides.lastSide,
            );
            const firstOffsetFreeB = offsetChainB
                ? CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(offsetChainB[0]!, sourceSides.firstSide)
                : null;
            const lastOffsetFreeB = offsetChainB
                ? CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(
                    offsetChainB[offsetChainB.length - 1]!,
                    sourceSides.lastSide,
                )
                : null;
            if (!firstOffsetFreeA || !lastOffsetFreeA
                || (isBidirectional && (!firstOffsetFreeB || !lastOffsetFreeB))) {
                continue;
            }

            // 相连端对象：切线不可用时的弦向回退依赖（首端=链首实体相连端，尾端=链尾实体相连端）
            const firstConnectedSource = CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(
                sourceChain[0]!,
                sourceSides.firstSide === 'start' ? 'end' : 'start',
            );
            const lastConnectedSource = CombineSketchEntitiesUnifiedDirection.getEntityEndpointBySide(
                sourceChain[sourceChain.length - 1]!,
                sourceSides.lastSide === 'start' ? 'end' : 'start',
            );
            if (!firstConnectedSource || !lastConnectedSource) {
                continue;
            }

            // 胶囊外侧方向：沿源实体在自由端处的切向延伸（远离链方向）——
            // 首端取链首实体自由端切向的反向，尾端取链尾实体自由端切向。
            // 不能用「自由端→相连端」弦向量近似：圆弧/椭圆弧端点处弦向与切向
            // 偏差大，半圆盖鼓包会朝内凹而非胶囊形外凸。
            const refPlane = this.referencePlane;
            const firstTangent = refPlane
                ? CombineSketchEntitiesUnifiedDirection.getEntityEndpointTangentWorld(
                    sourceChain[0]!,
                    sourceSides.firstSide,
                    refPlane,
                )
                : null;
            const lastTangent = refPlane
                ? CombineSketchEntitiesUnifiedDirection.getEntityEndpointTangentWorld(
                    sourceChain[sourceChain.length - 1]!,
                    sourceSides.lastSide,
                    refPlane,
                )
                : null;
            // 自由端侧为 start 时远离链方向 = 切向反向；为 end 时即切向本身
            const firstOutward = firstTangent
                ? firstTangent.multiplyScalar(sourceSides.firstSide === 'start' ? -1 : 1)
                : firstSourceFree.position.clone().sub(firstConnectedSource.position);
            const lastOutward = lastTangent
                ? lastTangent.multiplyScalar(sourceSides.lastSide === 'start' ? -1 : 1)
                : lastSourceFree.position.clone().sub(lastConnectedSource.position);

            connections.push({
                first: {
                    draftKey: first.key,
                    draftKeyB: second?.key ?? null,
                    startPoint: firstOffsetFreeA!,
                    endPoint: isBidirectional ? firstOffsetFreeB! : firstSourceFree,
                    outwardWorld: firstOutward,
                },
                last: {
                    draftKey: first.key,
                    draftKeyB: second?.key ?? null,
                    startPoint: lastOffsetFreeA!,
                    endPoint: isBidirectional ? lastOffsetFreeB! : lastSourceFree,
                    outwardWorld: lastOutward,
                },
            });
        }

        return connections;
    }

    /**
     * 创建顶端加盖预览 Line（仅开环链，预览阶段调用）。
     *
     * 说明：
     * 1. 复用阶段四登记的 coverChainRegistry 解析盖子连接（与落地
     *    {@link createCoverEntities} 同一数据源、同一解析链路），
     *    保证预览与落地盖子连接同一对端点、鼓包朝向一致；
     * 2. 只创建轻量 Three.js Line 预览，不创建真实 SketchLineObject/
     *    SketchArcObject，避免污染草图组（选择/约束求解/历史快照），
     *    与等距预览渲染深度策略的行为边界一致；
     * 3. 虚线标志与落地盖子实体的构造线口径一致：基本几何体/偏移几何体
     *    任一开启时落地盖子为构造线，预览同步以虚线呈现；
     * 4. 盖预览 Line 与偏移预览 Line 同生命周期（push 进 previewSketchEntities），
     *    clearPreviewSketchEntities / clearMode 自动统一移除。
     *
     * @param refPlane 当前激活草图组的参考面（由外层统一获取，避免重复读取）。
     */
    private createCoverPreviews(refPlane: ReferencePlane): void {
        // 防御守卫：未开启顶端加盖时直接返回（登记阶段同样有该门控）
        if (!this.options.cover.hasCover) {
            return;
        }

        // 与落地口径一致：构造几何体选项任一开启时盖子为构造线，预览以虚线样式呈现
        const dashed = this.options.isBasicGeometryBody || this.options.isOffsetGeometryBody;

        for (const { first, last } of this.resolveCoverConnections()) {
            const firstPreview = SketchIsometricEntitiesPreview.createCoverPreview(
                this.options.cover.type,
                refPlane,
                first.startPoint.getPosition(),
                first.endPoint.getPosition(),
                first.outwardWorld,
                dashed,
            );
            if (firstPreview) {
                this.previewSketchEntities.push(firstPreview);
                this.viewer.scene.add(firstPreview);
            }
            const lastPreview = SketchIsometricEntitiesPreview.createCoverPreview(
                this.options.cover.type,
                refPlane,
                last.startPoint.getPosition(),
                last.endPoint.getPosition(),
                last.outwardWorld,
                dashed,
            );
            if (lastPreview) {
                this.previewSketchEntities.push(lastPreview);
                this.viewer.scene.add(lastPreview);
            }
        }
    }

    /**
     * 创建单个盖子实体并登记进等距链草稿（供距离尺寸 metadata 持久化）。
     *
     * 说明：
     * 1. 起点始终为「第一条记录侧」端点（单向=本链偏移端点；双向=左偏移端点），
     *    终点为对侧端点（单向=源端点；双向=右偏移端点）；
     * 2. 双向时同一盖子同时登记进左右两条链的草稿（local/partner 互换），
     *    保证任一侧距离尺寸改值都能联动重建盖子；
     * 3. 直线盖登记 capType='straight line'，圆弧盖登记 capType='circularArc'。
     *
     * @param coverType 盖类型。
     * @param refPlane 参考面。
     * @param draftKey 第一条记录对应链的草稿键。
     * @param draftKeyB 第二条记录（双向）对应链的草稿键；单向为 null。
     * @param startPoint 盖端点 A（第一条记录侧）。
     * @param endPoint 盖端点 B（对侧）。
     * @param outwardWorld 圆弧盖胶囊外侧方向（世界坐标）。
     */
    private createCoverEntity(
        coverType: CoverType,
        refPlane: ReferencePlane,
        draftKey: string,
        draftKeyB: string | null,
        startPoint: SketchPointObject,
        endPoint: SketchPointObject,
        outwardWorld: Vector3,
    ): void {
        const cover = SketchOffsetCoverFactory.createCoverEntity({
            coverType,
            referencePlane: refPlane,
            startPoint,
            endPoint,
            outwardWorld,
        });

        // 构造几何体（基本几何体/偏移几何体任一选中）时，盖子实体同样转为构造线，
        // 与 SolidWorks 行为一致（用户在等距实体命令中选择了构造几何体选项）
        if (this.options.isBasicGeometryBody || this.options.isOffsetGeometryBody) {
            cover.isConstruction = true;
        }
        this.viewer.sketchGroupManager.addSketchObject(cover);

        // 登记盖子记录：本链侧端点（随本链距离重算更新）与对侧端点（单向=源端点；双向=配对链端点）
        const draft = this.offsetChainDraftRegistry.get(draftKey);
        if (draft) {
            draft.caps.push({
                capEntityId: cover.persistentId,
                capType: coverType,
                localEndpointId: startPoint.persistentId,
                partnerEndpointId: endPoint.persistentId,
            });
        }
        if (draftKeyB) {
            const draftB = this.offsetChainDraftRegistry.get(draftKeyB);
            if (draftB) {
                draftB.caps.push({
                    capEntityId: cover.persistentId,
                    capType: coverType,
                    localEndpointId: endPoint.persistentId,
                    partnerEndpointId: startPoint.persistentId,
                });
            }
        }
    }

    /**
     * 生成最终偏移后草图实体。
     * 
     * 说明：
     * 1. 直接遍历 offsetedSketchEntities（已由预览阶段通过 SketchEntityEquidistantOffset 创建）；
     * 2. 将每个偏移后的 SketchXxxObject 添加到草图组中完成最终渲染；
     * 3. 等距圆/圆弧/椭圆弧落地时与源实体圆心创建`重合`关系（受自动几何关系开关约束）；
     * 4. 闭合轮廓调用 syncContour() 同步上色。
     *
     * @returns 成功生成实体返回 true；无可用偏移实体返回 false。
     */
    generateOffsetSketchEntity(): boolean {
        // 判空保护：无偏移后实体时直接返回
        if (this.offsetedSketchEntities.length === 0) {
            // 落地失败：恢复预览阶段的「基本几何体」临时构造线，避免源实体样式残留
            this.restorePreviewBasicGeometryConstruction();
            this.viewer.drawingNoticeCallback?.(
                '无草图实体可以生成等距实体。',
                'warning',
            );
            return false;
        }

        // 顶端加盖：先于偏移实体循环创建盖子实体，确保 draft.caps 在链首距离尺寸
        // 组装 metadata（applyLineOffsetDistanceDimension → buildOffsetChainMetadata）之前就绪；
        // 盖子的端点对象在预览阶段已随偏移实体生成，此处仅创建封口实体并入组
        this.createCoverEntities();

        // 遍历已生成的偏移后 SketchXxxObject 实例，直接添加到草图组
        for (const entity of this.offsetedSketchEntities) {
            // 偏移几何体：新建偏移实体在入组前统一转为构造线（入组即呈现构造线材质）
            if (this.options.isOffsetGeometryBody && entity instanceof AbstractSketchEdgeObject) {
                entity.isConstruction = true;
            }
            const entityType = entity.objectType;

            switch (entityType) {
                case 'Line': {
                    const lineObj = entity as SketchLineObject;
                    this.viewer.sketchGroupManager.addSketchObject(lineObj);
                    // 单独添加起止控制点（与原行为保持一致）
                    this.viewer.addSketchObject(lineObj.startPoint);
                    this.viewer.addSketchObject(lineObj.endPoint);
                    // 等距线段与源线段先创建`平行`关系，再创建平行线距尺寸：
                    // 平行关系是 line-line-distance driving 尺寸成立的前提（否则距离方程
                    // 只钉住第二线起点到第一线的垂距，线段方向仍可自由旋转）
                    const lineSourceEntity = this.offsetSourceEntityMap.get(lineObj.uuid);
                    if (lineSourceEntity) {
                        this.applyLineOffsetParallelRelation(lineSourceEntity, lineObj);
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(lineObj.uuid);
                        this.applyLineOffsetDistanceDimension(lineSourceEntity, lineObj, !isChainHead);
                    }
                    break;
                }

                case 'Arc': {
                    const arcObj = entity as SketchArcObject;
                    this.viewer.sketchGroupManager.addSketchObject(arcObj);
                    // 等距圆弧与源圆弧圆心创建`重合`关系（对齐 SOLIDWORKS 等距实体自动关系；
                    // 关系由构造保证已满足，只挂载不求解，与切线弧自动相切关系同范式）
                    const arcSourceEntity = this.offsetSourceEntityMap.get(arcObj.uuid);
                    if (arcSourceEntity) {
                        this.applyCenterCoincidentRelation(arcSourceEntity, arcObj);
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(arcObj.uuid);
                        this.applyCurveOffsetDistanceDimension(arcSourceEntity, arcObj, !isChainHead);
                    }
                    break;
                }

                case 'Ellipse': {
                    const ellipseObj = entity as SketchEllipseObject;
                    const ellipseDef = ellipseObj.getEllipseDefinition();
                    const isFullEllipse = Math.abs(ellipseDef.endAngle - ellipseDef.startAngle - 2 * Math.PI) < 1e-6;

                    this.viewer.sketchGroupManager.addSketchObject(ellipseObj);
                    // 等距椭圆/椭圆弧的控制点需显式加入草图组才能渲染：
                    // SketchEllipseObject 不把控制点挂载为自身子节点，正常绘制椭圆
                    // （SketchEllipseDrawingHandler.addNewEllipseControlPoints）同样是
                    // 将控制点作为独立草图实体逐个加入；这里加入圆心 + 四个基准轴标记点，
                    // 椭圆弧（部分椭圆）另加入两个弧端点，与正常绘制的入组口径一致。
                    const addedPointIds = new Set<string>();
                    ellipseObj.getCardinalControlPointsSnapshot().forEach((point) => {
                        if (!addedPointIds.has(point.uuid)) {
                            addedPointIds.add(point.uuid);
                            this.viewer.addSketchObject(point);
                        }
                    });
                    if (!isFullEllipse) {
                        [ellipseObj.getStartPoint(), ellipseObj.getEndPoint()].forEach((point) => {
                            if (!addedPointIds.has(point.uuid)) {
                                addedPointIds.add(point.uuid);
                                this.viewer.addSketchObject(point);
                            }
                        });
                    }
                    // 等距椭圆/椭圆弧与源实体圆心创建`重合`关系（与圆弧分支同一范式）
                    const ellipseSourceEntity = this.offsetSourceEntityMap.get(ellipseObj.uuid);
                    if (ellipseSourceEntity) {
                        this.applyCenterCoincidentRelation(ellipseSourceEntity, ellipseObj);
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(ellipseObj.uuid);
                        this.applyCurveOffsetDistanceDimension(ellipseSourceEntity, ellipseObj, !isChainHead);
                    }
                    // 完整椭圆同步上色草图轮廓
                    if (isFullEllipse && this.viewer.shadedSketchContoursEnabled && ellipseObj.canCreateContour()) {
                        ellipseObj.syncContour();
                    }
                    break;
                }

                case 'Spline': {
                    const splineObj = entity as SketchSplineObject;
                    this.viewer.sketchGroupManager.addSketchObject(splineObj);
                    // 等距样条与源样条创建曲线偏移距离尺寸（isAddSize 门控；
                    // 拖动联动由 CurveOffsetDerivedSyncService 派生重算接管）
                    const splineSourceEntity = this.offsetSourceEntityMap.get(splineObj.uuid);
                    if (splineSourceEntity) {
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(splineObj.uuid);
                        this.applySplineOffsetDistanceDimension(splineSourceEntity, splineObj, !isChainHead);
                    }
                    // 闭合样条同步上色草图轮廓
                    const splineDef = splineObj.getCurveMetadata();
                    const isClosedSpline = splineDef.closureKind === 'closed';
                    if (isClosedSpline && this.viewer.shadedSketchContoursEnabled && splineObj.canCreateContour()) {
                        splineObj.syncContour();
                    }
                    break;
                }

                case 'Parabola': {
                    const parabolaObj = entity as SketchParabolaObject;
                    this.viewer.sketchGroupManager.addSketchObject(parabolaObj);
                    // 等距抛物线的控制点需显式加入草图组才能渲染并进入参数求解：
                    // SketchParabolaObject 不把控制点挂载为自身子节点，正常绘制抛物线
                    // 同样是把控制点作为独立草图实体逐个加入；这里加入焦点与顶点，
                    // 距离尺寸的顶点锚点与求解方程的焦点/顶点句柄都依赖这两点入组。
                    this.viewer.addSketchObject(parabolaObj.getFocalPoint());
                    this.viewer.addSketchObject(parabolaObj.getApexPoint());
                    // 等距抛物线与源抛物线只创建曲线偏移距离尺寸（isAddSize 门控），
                    // 不创建焦点重合等其他自动几何关系。
                    const parabolaSourceEntity = this.offsetSourceEntityMap.get(parabolaObj.uuid);
                    if (parabolaSourceEntity) {
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(parabolaObj.uuid);
                        this.applyParabolaOffsetDistanceDimension(parabolaSourceEntity, parabolaObj, !isChainHead);
                    }
                    break;
                }

                case 'Circle': {
                    const circleObj = entity as SketchCircleObject;
                    this.viewer.sketchGroupManager.addSketchObject(circleObj);
                    // 等距圆与源圆圆心创建`重合`关系（与圆弧/椭圆弧分支同一范式，
                    // 对齐 SOLIDWORKS 等距实体自动关系；关系由构造保证已满足，只挂载不求解）
                    const circleSourceEntity = this.offsetSourceEntityMap.get(circleObj.uuid);
                    if (circleSourceEntity) {
                        this.applyCenterCoincidentRelation(circleSourceEntity, circleObj);
                        // 组合图形每对实体都创建距离尺寸：链首显示智能尺寸 UI，其余隐藏（只约束不显示）。
                        const isChainHead = this.offsetChainFirstEntityUuids.has(circleObj.uuid);
                        this.applyCurveOffsetDistanceDimension(circleSourceEntity, circleObj, !isChainHead);
                    }
                    // 同步上色草图轮廓
                    if (this.viewer.shadedSketchContoursEnabled && circleObj.canCreateContour()) {
                        circleObj.syncContour();
                    }
                    break;
                }

                default:
                    this.viewer.drawingNoticeCallback?.(
                        `等距实体暂不支持该类型: ${entityType}`,
                        'warning',
                    );
                    break;
            }
        }

        // 基本几何体：参与成功偏移的源实体统一转为构造线（按 uuid 去重；
        // 偏移失败/整链丢弃的源实体不在 offsetSourceEntityMap 中，保持原样）
        if (this.options.isBasicGeometryBody) {
            const seenSourceIds = new Set<string>();
            for (const source of this.offsetSourceEntityMap.values()) {
                if (!(source instanceof AbstractSketchEdgeObject) || seenSourceIds.has(source.uuid)) {
                    continue;
                }
                seenSourceIds.add(source.uuid);
                source.isConstruction = true;
            }
        }

        // 源实体最终构造线状态已由本事务（before/after 整组快照）确定，
        // 丢弃预览阶段的临时恢复映射，避免随后的 clearPreviewSketchEntities
        // 恢复逻辑把落地结果改回普通线
        this.previewBasicGeometrySourceRestore.clear();

        // 闭合轮廓收口：必须在构造线转换之后、链草稿清理之前执行——
        // 1. 基本几何体/偏移几何体会把源/偏移/盖子转为构造线，收口候选过滤依赖最终构造线状态；
        // 2. 收口输入来自 offsetChainDraftRegistry（源链/偏移链/盖子持久 ID），清理后即丢失。
        this.finalizeClosedProfiles();

        // 渲染
        this.viewer.requestRender();
        this.clearPreviewSketchEntities(); // 移除预览草图实体
        // 清理偏移实体缓存，确保下次生成使用最新数据
        this.offsetedSketchEntities = [];
        this.offsetSourceEntityMap.clear();
        this.offsetChainFirstEntityUuids.clear();
        this.offsetChainDraftRegistry.clear();
        this.coverChainRegistry.clear(); // 同步清理顶端加盖链登记

        return true;
    }

    /**
     * 落地等距链的闭合轮廓收口。
     *
     * 说明：
     * 1. 从链落地草稿组装纯 ID 输入（源链/偏移链/盖子持久 ID），交给
     *    {@link SketchOffsetClosedProfileFinalizer} 走草图组统一闭合入口；
     * 2. 圆/整椭圆/闭合样条等单实体自闭合轮廓已在实体落地分支内各自 syncContour，
     *    不参与本收口；
     * 3. 无论上色草图轮廓开关是否开启都会创建闭合集合（profile 语义），
     *    开关只决定是否立即同步轮廓显示。
     */
    private finalizeClosedProfiles(): void {
        const sketchGroup = this.activeSketchGroup;
        // 收口依赖真实 SketchGroup 的对象树跟踪与闭合入口；非实例（测试桩等）
        // 或组缺失时跳过，与尺寸创建等落地分支对 activeSketchGroup 的防御口径一致
        if (!sketchGroup || !(sketchGroup instanceof SketchGroup) || this.offsetChainDraftRegistry.size === 0) {
            return;
        }

        this.closedProfileFinalizer.finalize({
            sketchGroup,
            chains: Array.from(this.offsetChainDraftRegistry.values()).map((draft) => ({
                sourceEntityIds: draft.sourceEntityIds,
                offsetEntityIds: draft.offsetEntityIds,
                capEntityIds: draft.caps.map((cap) => cap.capEntityId),
            })),
            syncContour: this.viewer.shadedSketchContoursEnabled,
        });
    }

    /**
     * 将候选实体列表中的 SketchCollectionObject 展开为组成它的具体草图实体。
     *
     * 说明：
     * 1. 等距实体不支持直接对 SketchCollectionObject 偏移：集合只是线/弧/样条等
     *    边对象的闭合宿主，偏移必须落到其组成实体上；
     * 2. 展开结果按 uuid 去重：集合成员可能同时被用户单独选中，避免重复参与偏移；
     * 3. 非集合实体原样保留，保持既有选择顺序与类型语义；
     * 4. 本方法同时服务于选中种子（{@link getPickedEntities}）与外部显式传入的
     *    选择链（{@link startMode} 的 options.sketchEntityChains），保证两条入口
     *    都统一收口“集合加入等距实体列表前先展开”的语义。
     *
     * @param candidates 待归一化的等距实体候选列表。
     * @returns 展开集合后的具体草图实体列表。
     */
    private normalizeOffsetCandidates(candidates: OffsetCandidate[]): OffsetCandidate[] {
        const normalized: OffsetCandidate[] = [];
        const seenIds = new Set<string>();

        for (const candidate of candidates) {
            // 集合对象：展开为其组成边（getLines 返回封闭集合的源边快照），
            // 集合成员均为可偏移的草图边实体
            if (isSketchCollectionObject(candidate)) {
                for (const member of candidate.getLines()) {
                    if (seenIds.has(member.uuid)) {
                        continue;
                    }
                    seenIds.add(member.uuid);
                    normalized.push(member);
                }
                continue;
            }

            // 普通候选实体：按 uuid 去重后原样保留
            if (seenIds.has(candidate.uuid)) {
                continue;
            }
            seenIds.add(candidate.uuid);
            normalized.push(candidate);
        }

        return normalized;
    }

    /**
     * 获取当前鼠标选中的草图实体对象，并按连通分量分组。
     * 
     * 说明：
     * 1. 从选择管理器读取当前用户点选的实体作为种子；
     * 2. 对每个未被访问的种子实体独立启动 BFS，通过端点拓扑遍历按连通分量分组；
     * 3. 开启选择链时：BFS 可扩展到所有相连实体（包括未选中的），
     *    自动扩展为完整选择链，同一连通分量的实体归入同一组；
     * 4. 未开启选择链时：BFS 被限制在已选中的实体集合内，仅将手动选中的相连实体
     *    归为一组（后续作为一个整体链处理，盖子/延伸对链内相邻实体生效），
     *    不会自动扩展选择到未选中的相连实体；
     * 5. 非边实体（如 Circle）没有端点，BFS 自然终止，独立成组；
     * 6. 构造线不参与选择链传播：邻接扩展与分叉计数均排除构造线
     *    （如中心矩形角点上的对角线构造线不计入分叉），
     *    被选中的构造线种子只组成自身单链，不向外扩展、也不会被其他链拉入；
     * 7. 自相交过滤：链内任意两实体存在非端点交点（checkSelfIntersection）时，
     *    该链视为自相交，从选择链中剔除（不参与等距实体偏移）；
     * 8. 分叉点截断：端点连接边数 > 2（分叉连接点，如 Y 形/十字形）时，分叉点之后的
     *    相邻草图丢弃、不再继续遍历，链在分叉点截断；被丢弃的分支若本身被选中，
     *    将作为独立链处理。
     */
    getPickedEntities() {
        // 从选择管理器读取当前用户点选的实体作为种子
        const rawSeeds = this.viewer.entitySelectionManager.getSelectedObjects() as OffsetCandidate[];

        // 种子归一化：若预选/点选中包含 SketchCollectionObject，先展开为其组成实体，
        // 保证等距实体列表只接收具体草图实体，集合本身不参与偏移
        const seeds = this.normalizeOffsetCandidates(rawSeeds);

        // 未开启选择链时，仅允许在已选中的实体之间传播分组；
        // 开启选择链时为 null，表示 BFS 可扩展到任意相连实体（包括未选中的）
        const seedSet = this.options.isOpenSelectChain
            ? null
            : new Set(seeds.map(s => s.uuid));

        // 全局已访问集合，跨所有连通分量的 BFS 共享，避免重复添加
        const visitedIds = new Set<string>();
        let chains: SketchEntityChain[] = [];

        // 对每个未被访问的种子实体独立启动 BFS，按连通分量分组
        for (const seed of seeds) {
            // 已被之前某次 BFS 覆盖（该种子与之前的种子属于同一连通分量），跳过
            if (visitedIds.has(seed.uuid)) continue;

            const chain: SketchEntityChain = [];
            chain.push(seed);
            visitedIds.add(seed.uuid);

            // 只有 AbstractSketchEdgeObject 才参与 BFS 拓扑扩展；
            // 构造线种子不向外传播（只组成自身单链），其余构造线由邻接表过滤排除
            const queue: AbstractSketchEdgeObject[] = [];
            if (seed instanceof AbstractSketchEdgeObject && !seed.isConstruction) {
                queue.push(seed);
            }

            // 广度优先遍历：从当前种子的端点出发，查找相连边并逐跳扩展
            while (queue.length > 0) {
                const currentEdge = queue.shift()!;

                // 取出当前边的两个端点（统一接口，适配所有边类型：Line/Arc/Circle/Ellipse/Spline 等）
                const endpoints = currentEdge.getRelatedSketchPointsSnapshot()
                    .filter((p) => p.isEndPoint)   // 只取端点角色（排除中点、圆心、象限点等）
                    .slice(0, 2);                   // 最多两个端点（Circle 可能没有端点）

                for (const endpoint of endpoints) {
                    // 利用 SketchPointObject 内置的邻接表做 O(1) 查找，而非遍历全场景所有边。
                    // 邻接表源头过滤构造线：构造线不参与选择链传播（不被扩展、也不计入分叉计数），
                    // 避免中心矩形角点"外框×2 + 对角线构造线×1"被误判为分叉点导致链截断。
                    // 分叉计数与下方扩展循环共用这份过滤后的列表，无需二次判重
                    const connectedEdges = endpoint.getConnectedEdgesSnapshot()
                        .filter((edge) => !edge.isConstruction);

                    // 分叉点检测：统计该端点处会被 BFS 实际扩展的有效相连边数。
                    // 未开启选择链（seedSet 非空）时，邻接表中未被选中的边会被下方
                    // seedSet 过滤、不会进入链，故不计入分叉判定；
                    // 开启选择链（seedSet 为 null）时全部计入，行为与现有一致
                    const effectiveConnectedEdgeCount = connectedEdges.filter(
                        (edge) => seedSet === null || seedSet.has(edge.uuid),
                    ).length;

                    // 分叉点检测：该端点连接的边数 > 2（含当前边自身，即至少 2 条分支）时，
                    // 该点为分叉连接点（Y 形/十字形等），分叉点之后的相邻草图全部丢弃：
                    // 不再从该端点扩展任何新边，链在分叉点截断。
                    // 被丢弃的分支保持未访问状态——若分支本身被选中，将作为独立链处理
                    if (effectiveConnectedEdgeCount > 2) {
                        continue;
                    }

                    for (const connectedEdge of connectedEdges) {
                        // 跳过已访问的边，防止环状连接导致无限循环
                        if (visitedIds.has(connectedEdge.uuid)) {
                            continue;
                        }

                        // 未开启选择链：只允许在已选中的实体之间分组，
                        // 不自动扩展选择到未选中的相连实体
                        if (seedSet && !seedSet.has(connectedEdge.uuid)) {
                            continue;
                        }

                        // 标记已访问
                        visitedIds.add(connectedEdge.uuid);
                        // 加入当前连通分量
                        chain.push(connectedEdge as OffsetCandidate);
                        // 加入队列继续扩展下一跳
                        queue.push(connectedEdge);
                    }
                }
            }

            chains.push(chain);
        }

        // 对每个选择链中的草图实体进行排序（未开启选择链时链内也可能含多个实体；
        // 单实体链排序函数直接返回副本，无副作用）
        for (let i = 0; i < chains.length; i++) {
            chains[i] = SketchEntitiesGroup.sketchEntitiesSort(chains[i]);
        }

        // 自相交链剔除：链内任意两实体存在非端点交点（checkSelfIntersection）时，
        // 该链视为自相交（如十字交叉、线穿过弧/曲线等），不参与等距实体偏移，从选择链中删除
        chains = chains.filter((chain) => {
            // 链内两两组合检查（链内实体数少，O(n²) 开销可接受）
            for (let i = 0; i < chain.length - 1; i++) {
                for (let j = i + 1; j < chain.length; j++) {
                    if (this.checkSelfIntersection(chain[i]!, chain[j]!)) {
                        return false; // 存在自相交：剔除整条链
                    }
                }
            }
            return true;
        });

        this.sketchEntityChains = chains;
    }

    /**
     * 检查选择链是否自相交（不包含草图端点重合的情况）。
     * 说明：
     * 1. 通过 {@link SketchEntityIntersect.computeEntityIntersections} 求两实体全部候选交点
     *    （样式样条按样条参与，槽口/集合等复合实体无交点）；
     * 2. 交点与任一实体的真实端点重合（相连/接触）时不计入自相交；
     * 3. 存在至少一个非端点交点即判定为自相交（如两线交叉、线穿过圆等）。
     *
     * @param sketchObj1 链内第一个草图实体。
     * @param sketchObj2 链内第二个草图实体。
     * @returns 存在非端点交点返回 true，否则返回 false。
     */
    checkSelfIntersection(sketchObj1: OffsetCandidate, sketchObj2: OffsetCandidate): boolean {
        // ① 求两实体全部候选交点（含端点重合的交点）
        const candidates = SketchEntityIntersect.computeEntityIntersections(sketchObj1, sketchObj2);
        if (candidates.length === 0) {
            return false;
        }

        // ② 收集两实体的真实端点（圆为封闭几何无端点，不参与端点豁免）
        const endpoints: Vector3[] = [
            ...this.collectEdgeEndpoints(sketchObj1),
            ...this.collectEdgeEndpoints(sketchObj2),
        ];

        // ③ 存在与任一端点都不重合的交点 → 判定自相交
        return candidates.some((candidate) =>
            !endpoints.some((endpoint) => endpoint.distanceToSquared(candidate.worldPoint) < 1e-16),
        );
    }

    /**
     * 应用「基本几何体」预览临时构造线。
     *
     * 说明：
     * 1. 勾选「基本几何体」后，预览阶段把参与偏移的源实体临时切换为构造线样式，
     *    实现与 SolidWorks 一致的即时反馈；
     * 2. 仅登记一次原值（首次切换时），后续重复生成预览不覆盖登记；
     * 3. 本方法只做预览期临时切换，源实体最终状态由落地事务快照决定。
     */
    private applyPreviewBasicGeometryConstruction(): void {
        if (!this.options.isBasicGeometryBody) {
            return;
        }
        for (const source of this.offsetSourceEntityMap.values()) {
            if (!(source instanceof AbstractSketchEdgeObject) || source.isConstruction) {
                continue;
            }
            if (!this.previewBasicGeometrySourceRestore.has(source.uuid)) {
                this.previewBasicGeometrySourceRestore.set(source.uuid, {
                    object: source,
                    originalIsConstruction: source.isConstruction,
                });
            }
            source.isConstruction = true;
        }
    }

    /**
     * 恢复「基本几何体」预览临时构造线。
     *
     * 说明：
     * 1. 重新生成预览前、退出等距实体命令时、落地失败时调用，
     *    把源实体恢复为进入预览前的 isConstruction 状态；
     * 2. 落地成功路径不调用本方法（先清空映射再清理预览），
     *    避免覆盖事务确定的最终构造线状态。
     */
    private restorePreviewBasicGeometryConstruction(): void {
        for (const record of this.previewBasicGeometrySourceRestore.values()) {
            record.object.isConstruction = record.originalIsConstruction;
        }
        this.previewBasicGeometrySourceRestore.clear();
    }

    // 清空预览草图实体
    clearPreviewSketchEntities() {
        for(const elem of this.previewSketchEntities) {
            this.viewer.scene.remove(elem);
        }
        // 分割段不入场景树，必须在此统一释放其 GPU 几何资源
        this.disposeSplitSegments();
        this.previewSketchEntities = [];
        this.previewOverlayDashLine.splitSegmentsMap = new Map();
    }

    /**
     * 收集草图实体的真实端点世界坐标（组合草图实体为线段/样条/椭圆弧/圆弧/抛物线）。
     * 说明：与 getPickedEntities 的端点读取方式一致（取 isEndPoint 角色控制点，
     * 排除圆心/中点/象限点等非端点角色）。
     * @param entity 草图实体。
     * @returns 端点世界坐标列表（至多 2 个；复合实体返回空数组）。
     */
    private collectEdgeEndpoints(entity: OffsetCandidate): Vector3[] {
        // 集合等复合实体不参与端点豁免（无端点协议）
        if (!(entity instanceof AbstractSketchEdgeObject)) {
            return [];
        }
        return entity.getRelatedSketchPointsSnapshot()
            .filter((point) => point.isEndPoint)
            .slice(0, 2)
            .map((point) => point.position);
    }

    /**
     * 释放全部分割段的 GPU 几何资源。
     * 说明：分割段只用于生成预览 Line，不入场景树，
     * 只能由本方法统一回收其 BufferGeometry，防止预览刷新时资源累积。
     */
    private disposeSplitSegments(): void {
        for (const segments of this.previewOverlayDashLine.splitSegmentsMap.values()) {
            for (const segment of segments) {
                if (segment instanceof AbstractSketchEdgeObject) {
                    segment.geometry.dispose();
                }
            }
        }
    }

    /**
     * 为等距线段与源线段创建`平行`关系。
     *
     * 说明：
     * 1. 平行关系是 `line-line-distance` driving 尺寸成立的前提——距离方程只约束
     *    第二线起点到第一线的垂距，缺少平行关系时线段方向仍是自由变量，
     *    拖动源线段后等距线段可能旋转而尺寸标注不变；
     * 2. 受"自动几何关系"开关约束，与圆心重合关系的门控口径一致；
     * 3. 关系由等距构造保证已满足，只挂载不求解，随等距实体落在同一历史事务内。
     *
     * @param sourceEntity 源实体（线段）。
     * @param offsetLine 等距线段。
     */
    private applyLineOffsetParallelRelation(sourceEntity: OffsetCandidate, offsetLine: SketchLineObject): void {
        // 自动几何关系关闭时不创建，与圆心重合关系同一门控
        const isAutoRelationsEnabled = this.viewer.options?.snapSettings?.isAutoGeometryRelationsEnabled ?? true;

        if (!isAutoRelationsEnabled) {
            return;
        }
        // 类型防御：映射异常时禁止把关系绑到无关实体
        if (!isSketchLineObject(sourceEntity)) {
            return;
        }

        // 同一条平行约束实例挂到两条线上（与关系创建服务点/线分支同一挂载方式）
        const parallelConstraint = new LineParallelConstraint();
        sourceEntity.constraints.addRelation(parallelConstraint);
        offsetLine.constraints.addRelation(parallelConstraint);
    }

    /**
     * 按链首落地实体 uuid 组装尺寸扩展元数据（等距链记录）。
     *
     * 说明：预览阶段已按链首 uuid 登记整链草稿；落地创建链首距离尺寸时调用本方法，
     * 把草稿转为尺寸 metadata 写入（随尺寸快照持久化，尺寸改值时供
     * OffsetChainDerivedSyncService 整链派生重算）。
     *
     * @param offsetEntityUuid 链首落地实体 uuid。
     * @returns 含等距链记录的尺寸元数据；无草稿时返回空对象。
     */
    private buildOffsetChainMetadata(offsetEntityUuid: string): { metadata?: SketchDimensionMetadata } {
        const draft = this.offsetChainDraftRegistry.get(offsetEntityUuid);

        if (!draft) {
            return {};
        }

        // 组装纯数据记录（草稿与 OffsetChainRecord 字段一致，直接复用即可；
        // 顶端加盖记录随链首尺寸 metadata 持久化，供距离改值时联动重建盖子）
        const offsetChain: OffsetChainRecord = {
            sourceEntityIds: [...draft.sourceEntityIds],
            offsetEntityIds: [...draft.offsetEntityIds],
            globalIsReverse: draft.globalIsReverse,
            caps: draft.caps.length > 0 ? [...draft.caps] : undefined,
        };
        return {
            metadata: {
                offsetChain,
            },
        };
    }

    /**
     * 为等距线段与源线段创建平行线距尺寸（`line-line-distance`）。
     *
     * 说明：
     * 1. 受 `options.isAddSize` 开关约束；等距构造保证两线平行且距离为偏移量，
     *    创建 driving 尺寸即锁定该距离，后续参数求解（拖动）经尺寸补充端口
     *    自动编入求解岛；
     * 2. 尺寸通过 Viewer 批量入口落库（不提交事务），补丁由父事务
     *    `resolveExtraPatches` 统一原子提交；
     * 3. 测量口径与智能尺寸推断一致：第二线起点到第一线的有向垂距，
     *    `directionSign` 记录偏移侧分支；标注位置取两线中点的中点沿法向偏移。
     *
     * @param sourceEntity 源实体（线段）。
     * @param offsetLine 等距线段。
     * @param hidden 是否隐藏智能尺寸 UI（组合链非链首实体传 true，仅保留约束）。
     */
    private applyLineOffsetDistanceDimension(
        sourceEntity: OffsetCandidate,
        offsetLine: SketchLineObject,
        hidden = false,
    ): void {
        // 未开启添加尺寸选项时不创建（对齐 SOLIDWORKS 等距实体选项）
        if (!this.options.isAddSize) {
            return;
        }
        // 类型防御：映射异常时禁止把尺寸绑到无关实体
        if (!isSketchLineObject(sourceEntity)) {
            return;
        }

        const referencePlane = this.referencePlane;
        const sketchGroup = this.activeSketchGroup;

        if (!referencePlane || !sketchGroup) {
            return;
        }

        // 在参考面局部 2D 中计算两线之间的有向距离（源线方向 × 等距线起点相对位移）
        const sourceStart = referencePlane.worldToLocal(sourceEntity.startPoint.getPosition());
        const sourceEnd = referencePlane.worldToLocal(sourceEntity.endPoint.getPosition());
        const offsetStart = referencePlane.worldToLocal(offsetLine.startPoint.getPosition());
        const offsetEnd = referencePlane.worldToLocal(offsetLine.endPoint.getPosition());
        const sourceDirection = new Vector2().subVectors(sourceEnd, sourceStart);
        const sourceLength = sourceDirection.length();

        if (sourceLength < 1e-12) {
            return;
        }
        sourceDirection.divideScalar(sourceLength);

        const offsetDirection = new Vector2().subVectors(offsetEnd, offsetStart);
        const offsetLength = offsetDirection.length();

        if (offsetLength < 1e-12) {
            return;
        }
        offsetDirection.divideScalar(offsetLength);

        // 非平行防御：截取/延伸处理保证等距线平行，异常拓扑下禁止生成非法尺寸
        if (Math.abs(1 - Math.abs(sourceDirection.dot(offsetDirection))) > 1e-6) {
            return;
        }

        const signedDistance = sourceDirection.x * (offsetStart.y - sourceStart.y)
            - sourceDirection.y * (offsetStart.x - sourceStart.x);

        if (Math.abs(signedDistance) < 1e-9) {
            return;
        }

        // 标注位置：两线中点的中点沿法向偏移固定边距（草图局部，米）
        const sourceMid = sourceStart.clone().add(sourceEnd).multiplyScalar(0.5);
        const offsetMid = offsetStart.clone().add(offsetEnd).multiplyScalar(0.5);
        const labelLocal = sourceMid.clone().add(offsetMid).multiplyScalar(0.5);
        const labelNormal = new Vector2(-sourceDirection.y, sourceDirection.x)
            .multiplyScalar((signedDistance >= 0 ? 1 : -1) * 0.01);
        labelLocal.add(labelNormal);

        const input: CreateSketchDimensionInput = {
            sketchId: sketchGroup.persistentId,
            dimensionKind: 'line-line-distance',
            role: 'driving',
            nominalValue: Math.abs(signedDistance),
            references: [
                {entityId: sourceEntity.persistentId, topologyRole: 'entity', selectionOrder: 0},
                {entityId: offsetLine.persistentId, topologyRole: 'entity', selectionOrder: 1},
            ],
            branch: {directionSign: signedDistance >= 0 ? 1 : -1},
            display: {
                labelPosition: [labelLocal.x, labelLocal.y],
                // 组合链非链首尺寸隐藏 UI：保留求解约束但不渲染标注
                ...(hidden ? {hidden: true} : {}),
            },
            // 组合图形整链联动：链首尺寸携带等距链记录
            ...this.buildOffsetChainMetadata(offsetLine.uuid),
        };

        // 批量落库，补丁挂起等待父事务统一提交
        this.pendingDimensionBatchResults.push(this.viewer.createSketchDimensionInBatch(input));
    }

    /**
     * 为等距圆/圆弧/椭圆（弧）与源实体创建曲线对偏移距离尺寸（`curve-offset-distance`）。
     *
     * 说明：
     * 1. 受 `options.isAddSize` 开关约束；
     * 2. 圆/圆弧测量半径差，椭圆测量长半轴差（等距构造保证长短半轴差同幅值同符号）；
     * 3. driving 方程由尺寸公式编译器按实体类型分派：圆/弧锁定半径差，
     *    椭圆锁定长轴平行 + 两半轴差（配合圆心重合关系即处处等距）；
     * 4. 补丁走批量入口、父事务统一提交（与线段平行线距同一链路）。
     *
     * @param sourceEntity 源实体。
     * @param offsetEntity 等距实体。
     * @param hidden 是否隐藏智能尺寸 UI（组合链非链首实体传 true，仅保留约束）。
     */
    private applyCurveOffsetDistanceDimension(
        sourceEntity: OffsetCandidate,
        offsetEntity: OffsetCandidate,
        hidden = false,
    ): void {
        // 未开启添加尺寸选项时不创建（对齐 SOLIDWORKS 等距实体选项）
        if (!this.options.isAddSize) {
            return;
        }

        const referencePlane = this.referencePlane;
        const sketchGroup = this.activeSketchGroup;

        if (!referencePlane || !sketchGroup) {
            return;
        }

        // 按实体类型解析半径差/半轴差与标注锚点（局部 2D）
        let difference: number | null = null;
        let firstAnchorLocal: Vector2 | null = null;
        let secondAnchorLocal: Vector2 | null = null;

        if (isSketchCircleObject(sourceEntity) && isSketchCircleObject(offsetEntity)) {
            // 圆：半径差，锚点取两圆右侧圆周点（同一径向）
            const firstRadius = sourceEntity.radius;
            const secondRadius = offsetEntity.radius;
            difference = secondRadius - firstRadius;
            const centerLocal = referencePlane.worldToLocal(sourceEntity.vec3dCenter);
            firstAnchorLocal = new Vector2(centerLocal.x + firstRadius, centerLocal.y);
            secondAnchorLocal = new Vector2(centerLocal.x + secondRadius, centerLocal.y);
        } else if (isSketchArcObject(sourceEntity) && isSketchArcObject(offsetEntity)) {
            // 圆弧：半径差，锚点取两弧起点（等距构造保证同参数角）
            const firstRadius = sourceEntity.getArcDefinition().radius;
            const secondRadius = offsetEntity.getArcDefinition().radius;
            difference = secondRadius - firstRadius;
            firstAnchorLocal = referencePlane.worldToLocal(sourceEntity.getStartPoint().getPosition());
            secondAnchorLocal = referencePlane.worldToLocal(offsetEntity.getStartPoint().getPosition());
        } else if (isSketchEllipseObject(sourceEntity) && isSketchEllipseObject(offsetEntity)) {
            // 椭圆：长半轴差，锚点取两椭圆弧扫过角域中点角上的点
            // （弧没画到长轴正向四分点时，初始标注不再悬在弧外）
            const firstRadiusX = sourceEntity.getEllipseDefinition().radiusX;
            const secondRadiusX = offsetEntity.getEllipseDefinition().radiusX;
            difference = secondRadiusX - firstRadiusX;

            firstAnchorLocal = this.resolveEllipseOffsetAnchorLocal(sourceEntity);
            secondAnchorLocal = this.resolveEllipseOffsetAnchorLocal(offsetEntity);

            if (!firstAnchorLocal || !secondAnchorLocal) {
                return;
            }
        }

        // 类型不匹配或几何退化（半径/半轴差为零）时不创建
        if (difference === null
            || firstAnchorLocal === null
            || secondAnchorLocal === null
            || Math.abs(difference) < 1e-9) {
            return;
        }

        // 标注位置：两锚点中点沿法向偏移固定边距（草图局部，米）
        const anchorDelta = secondAnchorLocal.clone().sub(firstAnchorLocal);
        const labelLocal = firstAnchorLocal.clone().add(secondAnchorLocal).multiplyScalar(0.5);

        if (anchorDelta.length() > 1e-12) {
            const labelNormal = new Vector2(-anchorDelta.y, anchorDelta.x).normalize().multiplyScalar(0.01);
            labelLocal.add(labelNormal);
        }

        const input: CreateSketchDimensionInput = {
            sketchId: sketchGroup.persistentId,
            dimensionKind: 'curve-offset-distance',
            role: 'driving',
            nominalValue: Math.abs(difference),
            references: [
                {entityId: sourceEntity.persistentId, topologyRole: 'entity', selectionOrder: 0},
                {entityId: offsetEntity.persistentId, topologyRole: 'entity', selectionOrder: 1},
            ],
            branch: {directionSign: difference >= 0 ? 1 : -1},
            display: {
                labelPosition: [labelLocal.x, labelLocal.y],
                // 组合链非链首尺寸隐藏 UI：保留求解约束但不渲染标注
                ...(hidden ? {hidden: true} : {}),
            },
            // 组合图形整链联动：链首尺寸携带等距链记录
            ...this.buildOffsetChainMetadata(offsetEntity.uuid),
        };

        // 批量落库，补丁挂起等待父事务统一提交
        this.pendingDimensionBatchResults.push(this.viewer.createSketchDimensionInBatch(input));
    }

    /**
     * 解析等距椭圆（弧）尺寸的初始锚点（草图局部 2D）。
     *
     * 说明：
     * 1. 锚点取椭圆弧扫过角域中点角上的点——椭圆弧不一定画到长轴正向四分点，
     *    初始标注必须落在实际画出的弧段上；完整椭圆返回长轴正向点（与旧口径一致）；
     * 2. 与渲染层布局解析共用 `resolveEllipseArcAnchor` 纯几何换算，保证
     *    创建时初始标注位置与"拖标注沿弧走"的锚点口径一致。
     *
     * @param entity 椭圆/椭圆弧实体。
     * @returns 弧域中点角锚点（局部 2D）；缺少长轴正向基准点时返回 null。
     */
    private resolveEllipseOffsetAnchorLocal(entity: SketchEllipseObject): Vector2 | null {
        const major = entity.getCardinalControlPointsSnapshot()
            .get(EllipseControlPointRole.MajorPositive);

        if (!major) {
            return null;
        }

        const definition = entity.getEllipseDefinition();
        const centerLocal = this.referencePlane.worldToLocal(entity.getCenterPoint().getPosition());
        const majorLocal = this.referencePlane.worldToLocal(major.getPosition());
        const geometry: SketchEllipseAnchorGeometry = {
            center: [centerLocal.x, centerLocal.y],
            major: [majorLocal.x, majorLocal.y],
            semiMinor: definition.radiusY,
            startAngle: definition.startAngle,
            endAngle: definition.endAngle,
            clockwise: definition.clockwise,
        };
        const anchor = resolveEllipseAnchorPointAtAngle(geometry, resolveEllipseArcMidAngle(geometry));

        return new Vector2(anchor[0], anchor[1]);
    }

    /**
     * 为等距抛物线与源抛物线创建曲线偏移距离尺寸（`curve-offset-distance`）。
     *
     * 说明：
     * 1. 受 `options.isAddSize` 开关约束；
     * 2. 等距抛物线的构造语义是「同轴、等焦距、顶点与焦点沿轴同步平移 d」，
     *    因此测量口径 = 第二抛物线顶点相对第一抛物线顶点在第一抛物线轴向上的
     *    有向距离，`directionSign` 记录偏移侧；标注位置取两顶点中点沿轴法向偏移；
     * 3. 只创建距离尺寸，不创建任何额外自动几何关系；
     * 4. 补丁走批量入口、父事务统一提交（与圆/弧/椭圆/样条同一链路）。
     *
     * @param sourceEntity 源抛物线实体。
     * @param offsetParabola 等距抛物线。
     * @param hidden 是否隐藏智能尺寸 UI（组合链非链首实体传 true，仅保留约束）。
     */
    private applyParabolaOffsetDistanceDimension(
        sourceEntity: OffsetCandidate,
        offsetParabola: SketchParabolaObject,
        hidden = false,
    ): void {
        // 未开启添加尺寸选项时不创建（对齐 SOLIDWORKS 等距实体选项）
        if (!this.options.isAddSize) {
            return;
        }
        // 类型防御：映射异常时禁止把尺寸绑到无关实体
        if (!isSketchParabolaObject(sourceEntity)) {
            return;
        }

        const referencePlane = this.referencePlane;
        const sketchGroup = this.activeSketchGroup;

        if (!referencePlane || !sketchGroup) {
            return;
        }

        // 在参考面局部 2D 中计算第二顶点沿第一抛物线轴向的有向距离
        const sourceFocal = referencePlane.worldToLocal(sourceEntity.getFocalPoint().getPosition());
        const sourceApex = referencePlane.worldToLocal(sourceEntity.getApexPoint().getPosition());
        const offsetApex = referencePlane.worldToLocal(offsetParabola.getApexPoint().getPosition());
        const axis = new Vector2().subVectors(sourceFocal, sourceApex);
        const axisLength = axis.length();

        if (axisLength < 1e-12) {
            return;
        }
        axis.divideScalar(axisLength);

        const apexDelta = new Vector2().subVectors(offsetApex, sourceApex);
        const signedDistance = apexDelta.dot(axis);

        if (Math.abs(signedDistance) < 1e-9) {
            return;
        }

        // 标注位置：两顶点中点沿轴法向偏移固定边距（草图局部，米）
        const labelLocal = new Vector2().addVectors(sourceApex, offsetApex).multiplyScalar(0.5);
        const labelNormal = new Vector2(-axis.y, axis.x)
            .multiplyScalar((signedDistance >= 0 ? 1 : -1) * 0.01);
        labelLocal.add(labelNormal);

        const input: CreateSketchDimensionInput = {
            sketchId: sketchGroup.persistentId,
            dimensionKind: 'curve-offset-distance',
            role: 'driving',
            nominalValue: Math.abs(signedDistance),
            references: [
                {entityId: sourceEntity.persistentId, topologyRole: 'entity', selectionOrder: 0},
                {entityId: offsetParabola.persistentId, topologyRole: 'entity', selectionOrder: 1},
            ],
            branch: {directionSign: signedDistance >= 0 ? 1 : -1},
            display: {
                labelPosition: [labelLocal.x, labelLocal.y],
                // 组合链非链首尺寸隐藏 UI：保留求解约束但不渲染标注
                ...(hidden ? {hidden: true} : {}),
            },
            // 组合图形整链联动：链首尺寸携带等距链记录
            ...this.buildOffsetChainMetadata(offsetParabola.uuid),
        };

        // 批量落库，补丁挂起等待父事务统一提交
        this.pendingDimensionBatchResults.push(this.viewer.createSketchDimensionInBatch(input));
    }

    /**
     * 为等距样条与源曲线创建曲线偏移距离尺寸（`curve-offset-distance`）。
     *
     * 说明：
     * 1. 受 `options.isAddSize` 开关约束；
     * 2. 名义值直接取构造偏移距离（`options.distance`，毫米→米）——等距尺寸的
     *    语义就是用户设置的等距距离；落地样条可能被封闭链延伸/截断重建，
     *    重建曲线偏离理论等距曲线，采样测量口径会得到 9.几 等失真值；
     * 3. 测量口径只用于确定 `directionSign`（偏移侧）与标注位置：优先从源首采样点
     *    沿首采样段法向发射线，与落地样条折线求交；无交点时退回首采样点投影；
     * 4. 源曲线支持样条/样式样条/抛物线——抛物线等距（方案A）输出样条；
     * 5. 求解器不为样条对编译方程——尺寸是源/等距样条的持久纽带，
     *    拖动联动由 CurveOffsetDerivedSyncService 按名义距离派生重算；
     * 6. 补丁走批量入口、父事务统一提交。
     *
     * @param sourceEntity 源曲线实体（普通样条、样式样条或抛物线）。
     * @param offsetSpline 等距样条。
     * @param hidden 是否隐藏智能尺寸 UI（组合链非链首实体传 true，仅保留约束）。
     */
    private applySplineOffsetDistanceDimension(
        sourceEntity: OffsetCandidate,
        offsetSpline: SketchSplineObject,
        hidden = false,
    ): void {
        // 未开启添加尺寸选项时不创建（对齐 SOLIDWORKS 等距实体选项）
        if (!this.options.isAddSize) {
            return;
        }
        // 类型防御：映射异常时禁止把尺寸绑到无关实体
        if (!(isSketchSplineObject(sourceEntity)
            || isSketchStyleSplineObject(sourceEntity)
            || isSketchParabolaObject(sourceEntity))) {
            return;
        }

        const referencePlane = this.referencePlane;
        const sketchGroup = this.activeSketchGroup;

        if (!referencePlane || !sketchGroup) {
            return;
        }

        const sourceCurvePoints = sourceEntity.getCurvePoints();
        const offsetCurvePoints = offsetSpline.getCurvePoints();

        if (sourceCurvePoints.length < 2 || offsetCurvePoints.length < 2) {
            return;
        }

        // 首采样段法向（正向 = 顺时针 90°），局部 2D
        const sourceFirst = referencePlane.worldToLocal(sourceCurvePoints[0]!);
        const sourceSecond = referencePlane.worldToLocal(sourceCurvePoints[1]!);
        const firstSegment = new Vector2().subVectors(sourceSecond, sourceFirst);
        const firstSegmentLength = firstSegment.length();

        if (firstSegmentLength < 1e-12) {
            return;
        }
        const offsetNormal = new Vector2(firstSegment.y, -firstSegment.x).divideScalar(firstSegmentLength);

        // 等距样条采样点列转到草图局部 2D（射线-折线求交使用）
        const offsetLocalPoints = offsetCurvePoints.map((point) => {
            const localPoint = referencePlane.worldToLocal(point);
            return new Vector2(localPoint.x, localPoint.y);
        });

        // 偏移侧判定与标注定位：从源首采样点沿法向发射线，与落地后的等距样条
        // 折线求交，取离源点最近的交点。封闭链的延伸/截断会重建样条、移动端点，
        // 但曲线主体上仍保留法向等距点的近似位置，交点足以判定偏移侧并定位标注；
        // 名义值不取交点距离（重建样条偏离理论等距曲线，交点距离会失真）。
        const forwardHit = this.intersectRayWithPolyline(sourceFirst, offsetNormal, offsetLocalPoints);
        // 法向正向无交点时，等距可能落在反向侧（isReverse 方向），试反向射线
        const backwardHit = forwardHit
            ? null
            : this.intersectRayWithPolyline(sourceFirst, offsetNormal.clone().negate(), offsetLocalPoints);

        let signedDistance: number;
        let labelLocal: Vector2;

        if (forwardHit) {
            signedDistance = forwardHit.hitDistance;
            // 标注位置：源首采样点与交点中点沿法向偏移固定边距（草图局部，米）
            labelLocal = new Vector2()
                .addVectors(sourceFirst, forwardHit.hitPoint)
                .multiplyScalar(0.5)
                .addScaledVector(offsetNormal, 0.01);
        } else if (backwardHit) {
            signedDistance = -backwardHit.hitDistance;
            // 标注位置：源首采样点与交点中点沿法向偏移固定边距
            labelLocal = new Vector2()
                .addVectors(sourceFirst, backwardHit.hitPoint)
                .multiplyScalar(0.5)
                .addScaledVector(offsetNormal, 0.01);
        } else {
            // 退化回退：法向射线与等距折线无交点（等距曲线被截断到射线之外）时，
            // 退回旧的首采样点投影口径，保证尺寸仍能创建而不是静默丢失。
            const offsetFirst = offsetLocalPoints[0]!;
            signedDistance = (offsetFirst.x - sourceFirst.x) * offsetNormal.x
                + (offsetFirst.y - sourceFirst.y) * offsetNormal.y;
            // 标注位置：两首采样点中点沿法向偏移固定边距
            labelLocal = new Vector2(
                (sourceFirst.x + offsetFirst.x) / 2,
                (sourceFirst.y + offsetFirst.y) / 2,
            ).addScaledVector(offsetNormal, 0.01);
        }

        if (Math.abs(signedDistance) < 1e-9) {
            return;
        }

        const input: CreateSketchDimensionInput = {
            sketchId: sketchGroup.persistentId,
            dimensionKind: 'curve-offset-distance',
            role: 'driving',
            // 名义值使用构造偏移距离（毫米→米）：等距尺寸语义 = 用户设置的等距距离。
            // 落地后的等距样条可能被封闭链延伸/截断重建，重建曲线会偏离理论等距
            // 曲线，若用重建后采样测量（求交/投影）会得到 9.几 等失真值。
            nominalValue: this.options.distance * 0.001,
            references: [
                {entityId: sourceEntity.persistentId, topologyRole: 'entity', selectionOrder: 0},
                {entityId: offsetSpline.persistentId, topologyRole: 'entity', selectionOrder: 1},
            ],
            branch: {directionSign: signedDistance >= 0 ? 1 : -1},
            display: {
                labelPosition: [labelLocal.x, labelLocal.y],
                // 组合链非链首尺寸隐藏 UI：保留求解约束但不渲染标注
                ...(hidden ? {hidden: true} : {}),
            },
            // 组合图形整链联动：链首尺寸携带等距链记录
            ...this.buildOffsetChainMetadata(offsetSpline.uuid),
        };

        // 批量落库，补丁挂起等待父事务统一提交
        this.pendingDimensionBatchResults.push(this.viewer.createSketchDimensionInBatch(input));
    }

    /**
     * 求 2D 射线与折线的最近正向交点。
     *
     * 说明：
     * 1. 用于等距样条距离尺寸的偏移侧判定与标注锚点定位——从源首采样点沿法向
     *    发射线，与等距样条 `getCurvePoints` 采样折线求交，交点用于确定
     *    `directionSign` 与标注位置（名义值仍取构造偏移距离，见调用方）；
     * 2. 逐段求解 `origin + t·direction = a + u·(b - a)` 的 2×2 线性系统，
     *    只接受段内（u∈[0,1]）且射线正向（t≥0）的解，并取 t 最小的交点；
     * 3. 无交点时返回 null，由调用方走退化回退口径。
     *
     * @param rayOrigin 射线起点（草图局部 2D）。
     * @param rayDirection 射线单位方向（草图局部 2D）。
     * @param polyline 折线顶点（草图局部 2D）。
     * @returns 最近正向交点与距离；无交点返回 null。
     */
    private intersectRayWithPolyline(
        rayOrigin: Vector2,
        rayDirection: Vector2,
        polyline: readonly Vector2[],
    ): { hitPoint: Vector2; hitDistance: number } | null {
        // 平行/退化线段行列式阈值（草图单位为米，线段长度通常远大于该值）
        const PARALLEL_DETERMINANT_EPSILON = 1e-14;
        // 段参数与射线参数的外扩容差，吸收采样折线的端点舍入
        const PARAMETER_EPSILON = 1e-9;

        let bestDistance = Number.POSITIVE_INFINITY;
        let bestPoint: Vector2 | null = null;

        for (let index = 0; index < polyline.length - 1; index++) {
            const segmentStart = polyline[index]!;
            const segmentEnd = polyline[index + 1]!;
            const segment = new Vector2().subVectors(segmentEnd, segmentStart);

            // 解 segmentStart + u·segment = rayOrigin + t·rayDirection，
            // 即 u·segment + t·(-rayDirection) = rayOrigin - segmentStart
            const determinant = segment.x * (-rayDirection.y) - segment.y * (-rayDirection.x);
            if (Math.abs(determinant) < PARALLEL_DETERMINANT_EPSILON) {
                continue;
            }

            const originToSegmentStart = new Vector2().subVectors(rayOrigin, segmentStart);
            const u = (-originToSegmentStart.x * rayDirection.y + originToSegmentStart.y * rayDirection.x)
                / determinant;
            const t = (segment.x * originToSegmentStart.y - segment.y * originToSegmentStart.x)
                / determinant;

            // 只接受段内交点与射线正向交点
            if (u < -PARAMETER_EPSILON || u > 1 + PARAMETER_EPSILON || t < -PARAMETER_EPSILON) {
                continue;
            }

            if (t < bestDistance) {
                bestDistance = t;
                bestPoint = new Vector2().copy(rayOrigin).addScaledVector(rayDirection, t);
            }
        }

        if (!bestPoint) {
            return null;
        }
        return { hitPoint: bestPoint, hitDistance: bestDistance };
    }

    /**
     * 移交本次落地批次产生的尺寸领域补丁（供父事务 `resolveExtraPatches` 使用）。
     *
     * 说明：移交后保留对称回滚句柄，父事务提交失败时由
     * {@link rollbackPendingDimensions} 消费；补丁只移交一次，避免重复提交。
     *
     * @returns 尺寸创建领域补丁列表。
     */
    public takePendingDimensionPatches(): readonly EntityPatch[] {
        const patches = this.pendingDimensionBatchResults.flatMap((result) => [...result.patches]);

        // 仅有待移交批次时才替换在途回滚句柄；空批次重复调用不得清掉
        // 上一笔已移交、仍在等待父事务提交结果的回滚句柄
        if (this.pendingDimensionBatchResults.length > 0) {
            this.inFlightDimensionRollbacks = this.pendingDimensionBatchResults.map((result) => result.rollback);
            this.pendingDimensionBatchResults = [];
        }

        return patches;
    }

    /**
     * 对称回滚本次落地批次已移交的尺寸写入（供父事务 `rollbackExtraState` 使用）。
     *
     * 说明：历史提交失败时草图组运行态由父事务恢复，尺寸仓库等额外领域状态
     * 必须由本方法同步回滚，避免出现"几何已恢复但尺寸仍残留"的半截事务。
     */
    public rollbackPendingDimensions(): void {
        this.inFlightDimensionRollbacks.forEach((rollback) => rollback());
        this.pendingDimensionBatchResults.forEach((result) => result.rollback());
        this.inFlightDimensionRollbacks = [];
        this.pendingDimensionBatchResults = [];
    }

    /**
     * 为等距圆/圆弧/椭圆弧与源实体创建圆心`重合`关系。
     *
     * 说明：
     * 1. 对齐 `SketchArcDrawingHandler.applyTangentArcSourceRelation` 的自动关系范式：
     *    关系由偏移构造保证已满足（圆心未移动），只挂载约束、不触发求解，
     *    避免创建瞬间挪动几何；
     * 2. 受"自动几何关系"开关约束；关系随等距实体落在同一历史事务内，
     *    撤销/持久化由外层 `sketch.runtime.offset-entities` 事务收口；
     * 3. 只有源实体与等距实体类型一致（圆/圆弧/椭圆弧各自配对）时才创建，
     *    防止映射异常时把关系绑到无关实体上。
     *
     * @param sourceEntity 源实体（圆/圆弧或椭圆弧）。
     * @param offsetEntity 等距实体（圆/圆弧或椭圆弧）。
     */
    private applyCenterCoincidentRelation(sourceEntity: OffsetCandidate, offsetEntity: OffsetCandidate): void {
        // 自动几何关系关闭时不创建，与绘制命令的自动关系行为保持一致
        const isAutoRelationsEnabled = this.viewer.options?.snapSettings?.isAutoGeometryRelationsEnabled ?? true;
        if (!isAutoRelationsEnabled) {
            return;
        }

        // 圆/圆弧/椭圆弧各自配对，且要求源/目标类型一致
        if (isSketchCircleObject(sourceEntity) && isSketchCircleObject(offsetEntity)) {
            this.bindCenterCoincidentConstraint(
                sourceEntity.getCenterControlPoint(),
                offsetEntity.getCenterControlPoint(),
            );
            return;
        }
        if (isSketchArcObject(sourceEntity) && isSketchArcObject(offsetEntity)) {
            this.bindCenterCoincidentConstraint(sourceEntity.getCenterPoint(), offsetEntity.getCenterPoint());
            return;
        }
        if (isSketchEllipseObject(sourceEntity) && isSketchEllipseObject(offsetEntity)) {
            this.bindCenterCoincidentConstraint(sourceEntity.getCenterPoint(), offsetEntity.getCenterPoint());
            return;
        }
    }

    /**
     * 在两个圆心控制点上挂载同一条点点重合约束。
     *
     * 说明：`SketchRelationContainer.addRelation` 会把持有者自动绑定为约束目标，
     * 两个点先后挂载同一条约束实例后即形成双目标重合关系，与
     * `SketchRelationCreationService.createMultiRelation` 的点关系分支挂载方式一致。
     *
     * @param sourceCenterPoint 源实体圆心控制点。
     * @param offsetCenterPoint 等距实体圆心控制点。
     */
    private bindCenterCoincidentConstraint(
        sourceCenterPoint: SketchPointObject,
        offsetCenterPoint: SketchPointObject,
    ): void {
        const coincidentConstraint = new PointCoincidentConstraint();
        sourceCenterPoint.constraints.addRelation(coincidentConstraint);
        offsetCenterPoint.constraints.addRelation(coincidentConstraint);
    }
}
